import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { fetchQuotes, syncQuotes, saveQuoteComment, saveQuoteLineStatus, manageQuoteUrl, deleteQuoteComment } from '../../context/CommonContex'
import messages from '../../statics/messages.json';
import viewIcon from '../../statics/images/view.png';
import filterIcon from '../../statics/images/filter.png';
import excelIcon from '../../statics/images/excel.png';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import HoverPO from '../HoverPO';
import OrderCommentsButton from '../comments/OrderCommentsButton';
import StickyNotesCell from '../StickyNotes/StickyNotesCell';
import useStickyNotes from '../StickyNotes/useStickyNotes';
import useShippingInstructions from '../StickyNotes/useShippingInstructions';
import { getCommentThreadKey, useCommentDock } from '../../context/CommentDockContext';
import { useNotifications } from '../../context/NotificationsContext';
import OrderRefresh from './OrderRefresh';
import FloatingRefreshButton from './FloatingRefreshButton';
import useAutoRefreshTimer, { AUTO_REFRESH_INTERVAL_SECONDS } from './useAutoRefreshTimer';
import OrderStatusDropdown, { orderStatusMenuPosition } from './OrderStatusDropdown';
import ColumnVisibilityModal from '../Common/ColumnVisibilityModal';
import ColumnGroupsModal from '../Common/ColumnGroupsModal';
import CombinedColumnCell from '../Orders/CombinedColumnCell';
import ViewModeToggle from '../Common/ViewModeToggle';
import PresenceBar from '../Common/PresenceBar';
import CellPresence from '../Common/CellPresence';
import usePresence from '../../hooks/usePresence';
import useRowPresence from '../../hooks/useRowPresence';
import useFocusedCell from '../../hooks/useFocusedCell';
import { cellKey, mergeRowPresence } from '../../utils/presenceKeys';
import { getCommentId, isQuoteLikeRecord, isTempCommentId, toStoredComment } from '../../utils/commentText';
import {
    loadInlineDropdownModeFromPreference,
    loadViewModeDefault,
    saveViewModeDefault,
    isInlineViewMode,
} from '../../utils/viewModePreference';
import {
    DEFAULT_QUOTES_COLUMN_ORDER,
    QUOTES_COLUMN_ORDER_STORAGE_KEY,
    COLUMN_LABELS,
    GROUPABLE_COLUMN_IDS,
    NORMAL_GROUP_ID,
    QUOTE_SET_GROUP_ID,
    QUOTE_SET_MERGED_ID,
    loadColumnOrder,
    loadColumnVisibility,
    saveColumnVisibility,
    getQuotesTableWidth,
    getStartupColumnGroupsState,
    saveColumnGroupsState,
    getEffectiveColumnOrder,
    buildEffectiveColumnOrder,
    expandMergedColumnOrder,
    getMergedIdForGroup,
    getSourceIdsForGroup,
    isMergedColumnId,
    sanitizeColumnOrder,
} from './quotesColumnConfig';
import * as XLSX from 'xlsx';
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

import '../../App.css';
import './Quotes.css';
// Shared page-title wrapper styles (.order-validation-outer / .order-validation-page-title-wrap)
import './OrderValidation.css';

/** Quote Status Tags (same list/colors as Orders' ORDER_STATUS_TAGS, per parity decision). */
const QUOTE_STATUS_TAGS = [
    { label: 'CC Hold', color: '#E39827' },
    { label: 'Wire Hold', color: '#E39827' },
    { label: 'Credit Hold', color: '#E39827' },
    { label: 'PPA', color: '#E39827' },
    { label: 'Paid', color: '#28a745' },
    { label: 'Partial', color: '#EF9884' },
    { label: 'May Ship Early', color: '#6FB169' },
    { label: 'CoT Required', color: '#4AB2D5' },
    { label: 'PMA', color: '#4AB2D5' },
    { label: 'AS9102', color: '#4AB2D5' },
    { label: 'Burn Test', color: '#4AB2D5' },
    { label: 'No Overage', color: '#BD83B8' },
    { label: 'Specific Lengths Required', color: '#BD83B8' },
    { label: 'Do Not Insure', color: '#BD83B8' },
    { label: 'Shipping Date Required', color: '#BD83B8' },
    { label: 'No Charge', color: '#BD83B8' },
    { label: 'AOG', color: '#5C4033' },
    { label: 'Hot Order', color: '#FF6B35', icon: 'fa fa-fire' },
    { label: 'PO Review', color: '#ffc400' }
];

/** Quote Line Status Tags (same list/colors as Orders' LINE_STATUS_TAGS, per parity decision). */
const QUOTE_LINE_STATUS_TAGS = [
    { label: 'Released', color: '#02f77c' },
    { label: 'Shipped', color: '#2D895F' },
    { label: 'In Kitting', color: '#CB5722' },
    { label: 'In Process', color: '#9288FF' },
    { label: 'In Testing', color: '#84E6F8' },
    { label: 'In Outgoing Inspection', color: '#B8B2E1' },
    { label: 'Not Released', color: '#F3DC6B' },
    { label: 'On Hold Cart', color: '#C56A00' },
    { label: 'Staging Cart', color: '#1DA1DB' },
    { label: 'Waiting On Components', color: '#A40202' },
    { label: 'PMA', color: '#594E9F' },
    { label: 'RMA', color: '#EB4F4F' },
    { label: 'ENG Review', color: '#625C5C' },
    { label: 'Charge', color: '#1d545e' }
];

function ReorderableCells({ order, draggable = false, dragHandlers, draggedColumnId, mergedCells = {}, columnVisibility = {}, onToggleColumn, isHeader = false, children }) {
    const cells = React.Children.toArray(children).filter(
        (child) => React.isValidElement(child) && (typeof child.type === 'string' || typeof child.type === 'function')
    );
    if (cells.length !== DEFAULT_QUOTES_COLUMN_ORDER.length) {
        return <>{cells}</>;
    }
    const byId = {};
    DEFAULT_QUOTES_COLUMN_ORDER.forEach((id, i) => { byId[id] = cells[i]; });
    const activeOrder = Array.isArray(order) && order.length ? order : DEFAULT_QUOTES_COLUMN_ORDER;
    const visibleCount = activeOrder.filter((cid) => columnVisibility[cid] !== false).length;
    return (
        <>
            {activeOrder.map((id) => {
                const cell = isMergedColumnId(id) ? mergedCells?.[id] : byId[id];
                if (!React.isValidElement(cell) || (typeof cell.type !== 'string' && typeof cell.type !== 'function')) {
                    return null;
                }
                if (columnVisibility[id] === false) {
                    if (isHeader) {
                        return (  
                            <th
                                key={id}
                                className="gf-col-hidden-marker"
                                title="Show hidden column"
                                onClick={(e) => { e.stopPropagation(); onToggleColumn && onToggleColumn(id); }}
                            >
                                <span className="gf-col-hidden-bar" aria-label="Show hidden column"></span>
                            </th>
                        );
                    }
                    return (
                        <td key={id} className="gf-col-hidden-marker">
                            <span className="gf-col-hidden-bar"></span>
                        </td>
                    );
                }
                let renderCell = cell;
                if (isHeader && onToggleColumn && visibleCount > 1) {
                    const edgeHandle = (
                        <span
                            key="gf-hide-edge"
                            className="gf-col-hide-edge"
                            role="button"
                            title="Hide column"
                            aria-label="Hide column"
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); onToggleColumn(id); }}
                        ></span>
                    );
                    const existingChildren = React.Children.toArray(cell.props.children);
                    renderCell = React.cloneElement(
                        cell,
                        { style: { ...(cell.props.style || {}), position: 'relative' } },
                        ...existingChildren,
                        edgeHandle
                    );
                }
                if (!draggable || !dragHandlers) {
                    return React.cloneElement(renderCell, { key: id });
                }
                const isDragging = draggedColumnId === id;
                return React.cloneElement(renderCell, {
                    key: id,
                    draggable: true,
                    onDragStart: (e) => dragHandlers.onDragStart(e, id),
                    onDragOver: (e) => dragHandlers.onDragOver(e, id),
                    onDrop: (e) => dragHandlers.onDrop(e, id),
                    onDragEnd: dragHandlers.onDragEnd,
                    title: 'Drag to reorder column',
                    style: {
                        ...(renderCell.props.style || {}),
                        cursor: 'grab',
                        opacity: isDragging ? 0.4 : 1,
                    },
                });
            })}
        </>
    );
}

/** Prefer quote.quoted_on (Cetec timestamp); fall back to earliest line created_on. */
function getQuoteStageDateTime(quote) {
    const quotedOn = quote?.quoted_on || quote?.quotedOn;
    if (quotedOn) return quotedOn;
    if (!quote?.lines?.length) return null;
    const dates = quote.lines.map((line) => line.created_on).filter(Boolean).sort();
    return dates.length > 0 ? dates[0] : null;
}

function formatQuotedOnTime(dateTimeString) {
    if (!dateTimeString) return '';
    const match = String(dateTimeString).match(/(\d{1,2}):(\d{2})/);
    if (match) {
        let hour = parseInt(match[1], 10);
        const minute = match[2];
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        return `${hour}:${minute} ${ampm}`;
    }
    const parsed = new Date(dateTimeString);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatTimeInStageLabel(dateTimeString) {
    if (!dateTimeString) return '-';
    try {
        const created = new Date(dateTimeString);
        if (Number.isNaN(created.getTime())) return '-';
        const today = new Date();
        created.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
        const days = diffDays <= 0 ? 1 : diffDays;
        const time = formatQuotedOnTime(dateTimeString);
        return time ? `${days} Day / ${time}` : `${days} Day`;
    } catch (e) {
        return '-';
    }
}

/** Days in Time in Stage from quoted_on, else earliest line created_on. */
function getQuoteDaysInStage(quote) {
    const dateTime = getQuoteStageDateTime(quote);
    if (!dateTime) return 0;
    try {
        const created = new Date(dateTime);
        const today = new Date();
        created.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays <= 0 ? 1 : diffDays;
    } catch (e) {
        return 0;
    }
}

/** Match Orders / Order Validation: drop the 3-letter prefix from a part number. */
function formatPartNumber(value) {
    const partNumber = String(value || '');
    if (!partNumber || partNumber === '-') return '-';
    return partNumber.length >= 3 ? partNumber.substring(3) : partNumber;
}

const TIME_IN_STAGE_ONE_PAGE_SIZE = 50;
const TIME_IN_STAGE_ONE_MAX_PAGES = 16;
const TIME_IN_STAGE_DEFAULT_DAYS = new Set([1, 2]);
const TIME_IN_STAGE_SCAN_BATCH = 4;

function matchesDefaultTimeInStage(quote) {
    return TIME_IN_STAGE_DEFAULT_DAYS.has(getQuoteDaysInStage(quote));
}

/** Presence room id for this page (matches the route path). */
const PRESENCE_PAGE = 'quotes';

const Quotes = () => {
    const navigate = useNavigate();
    // Live presence: who else is on this page / in which cell.
    const presence = usePresence(PRESENCE_PAGE, {
        onLockDenied: (evt) =>
            console.info(`[presence] ${evt?.owner?.name || 'Someone'} is already editing ${evt?.cell}`),
        onRemoteSave: (evt) => applyRemoteSave(evt),
    });

    const quoteLineCell = useCallback(
        (lineId) => cellKey(PRESENCE_PAGE, 'line', lineId, 'lineStatus'),
        []
    );

    // Quote Status lives in the collapsed grid row, so its presence
    // has to work without anyone expanding the quote.
    const quoteStatusCell = useCallback(
        (quoteId) => cellKey(PRESENCE_PAGE, 'order', quoteId, 'quoteStatus'),
        []
    );

    const quoteNotesCell = useCallback(
        (quoteId) => cellKey(PRESENCE_PAGE, 'order', quoteId, 'comment'),
        []
    );

    // Destructured so these identities only change when the roster does, not on
    // every render of this (very large) grid.
    const {
        usersInCell: presenceUsersInCell,
        lockOwner: presenceLockOwner,
        reportFocus: presenceFocus,
        reportBlur: presenceBlur,
    } = presence;

    const commentDock = useCommentDock();
    const notificationsCtx = useNotifications();

    const quoteLineLockOwner = useCallback(
        (lineId) => presenceLockOwner(quoteLineCell(lineId)),
        [presenceLockOwner, quoteLineCell]
    );

    const quoteStatusLockOwner = useCallback(
        (quoteId) => presenceLockOwner(quoteStatusCell(quoteId)),
        [presenceLockOwner, quoteStatusCell]
    );

    const renderQuoteLinePresence = useCallback(
        (lineId) => {
            const key = quoteLineCell(lineId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    owner={presenceLockOwner(key)}
                    compact
                    activity="on this line status"
                />
            );
        },
        [presenceUsersInCell, presenceLockOwner, quoteLineCell]
    );

    const renderQuoteStatusPresence = useCallback(
        (quoteId) => {
            const key = quoteStatusCell(quoteId);
            return (
                <CellPresence
                    users={presenceUsersInCell(key)}
                    owner={presenceLockOwner(key)}
                    size={16}
                    compact
                    activity="changing this quote status"
                />
            );
        },
        [presenceUsersInCell, presenceLockOwner, quoteStatusCell]
    );

    const [quotesData, setQuotesData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedQuoteDetails, setSelectedQuoteDetails] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [commentsMap, setCommentsMap] = useState({});
    const [modalLoading, setModalLoading] = useState(false);
    const [defaultViewMode, setDefaultViewMode] = useState(() => loadViewModeDefault(localStorage.getItem('userEmail')));
    const [inlineDropdownMode, setInlineDropdownMode] = useState(() => loadInlineDropdownModeFromPreference(localStorage.getItem('userEmail')));
    const [expandedInlineQuotes, setExpandedInlineQuotes] = useState({}); // Track expanded quotes in inline mode
    const [inlinePartDropdown, setInlinePartDropdown] = useState({
        open: false,
        top: 0,
        left: 0,
        partId: null
    });

    const handleSelectView = useCallback((mode) => {
        setInlineDropdownMode(isInlineViewMode(mode));
    }, []);

    const handleSetViewModeDefault = useCallback((mode) => {
        const userEmail = localStorage.getItem('userEmail');
        saveViewModeDefault(userEmail, mode);
        setDefaultViewMode(mode);
        setInlineDropdownMode(isInlineViewMode(mode));
    }, []);

    // Quote Status dropdown state (keyed by quote.id)
    const [quoteStatusDropdownOpenId, setQuoteStatusDropdownOpenId] = useState(null);
    // Quote whose notes popup (HoverPO) is open, so presence can claim that cell.
    const [openNotesQuoteIds, setOpenNotesQuoteIds] = useState(() => new Set());
    const [quoteStatusDropdownPosition, setQuoteStatusDropdownPosition] = useState({ top: 0, left: 0 });
    const [quoteStatusSelections, setQuoteStatusSelections] = useState({});
    const [submittedQuoteStatuses, setSubmittedQuoteStatuses] = useState({});
    const [quoteTagsToDeselect, setQuoteTagsToDeselect] = useState({});

    // Quote Line Status dropdown state (keyed by quote line id)
    const [quoteLineStatusDropdownOpenId, setQuoteLineStatusDropdownOpenId] = useState(null);
    const [quoteLineStatusDropdownPosition, setQuoteLineStatusDropdownPosition] = useState({ top: 0, left: 0 });
    const [quoteLineStatusSelections, setQuoteLineStatusSelections] = useState({});
    const [submittedQuoteLineStatuses, setSubmittedQuoteLineStatuses] = useState({});
    const [quoteLineTagsToDeselect, setQuoteLineTagsToDeselect] = useState({});

    // Quote Access (manage links) state
    const [quoteAccessLinks, setQuoteAccessLinks] = useState({});
    const [showQuoteAccessModal, setShowQuoteAccessModal] = useState(false);
    const [selectedQuoteForAccess, setSelectedQuoteForAccess] = useState(null);
    const [newQuoteLinkUrl, setNewQuoteLinkUrl] = useState('');
    const [newQuoteLinkUrlName, setNewQuoteLinkUrlName] = useState('');

    // Refresh functionality state
    const [lastRefreshData, setLastRefreshData] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const titleWrapRef = useRef(null);
    const tableScrollRef = useRef(null);
    const quotesFetchGenerationRef = useRef(0);

    // Filter functionality state
    const [filterTabOpen, setFilterTab] = useState(false);
    const [exports, setExports] = useState(false);
    const [createdOnDate, setCreatedOnDate] = useState('');
    const [shippingDate, setShippingDate] = useState('');
    
    // Filter input states
    const [filterQuoteId, setFilterQuoteId] = useState('');
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterOwner, setFilterOwner] = useState('');
    const [filterQuotePrice, setFilterQuotePrice] = useState('');

    // Default on open: Quote descending (largest number first). Newer quotes
    // also put Created On newest-first (current date at top, older dates below).
    const [columnSort, setColumnSort] = useState({ key: 'quote', direction: 'descending' });

    // Draggable column reorder + column groups
    const [columnOrder, setColumnOrder] = useState(() => {
        const groups = getStartupColumnGroupsState(localStorage.getItem('userEmail'));
        const base = loadColumnOrder();
        return getEffectiveColumnOrder(base, groups.activeGroupId, groups.customGroups);
    });
    const [draggedColumnId, setDraggedColumnId] = useState(null);

    const [showColumnGroupsModal, setShowColumnGroupsModal] = useState(false);
    const [columnGroupsState, setColumnGroupsState] = useState(() => {
        return getStartupColumnGroupsState(localStorage.getItem('userEmail'));
    });
    const { activeGroupId, defaultGroupId, customGroups } = columnGroupsState;

    const effectiveColumnOrder = useMemo(
        () => getEffectiveColumnOrder(columnOrder, activeGroupId, customGroups),
        [columnOrder, activeGroupId, customGroups]
    );

    // Column show/hide (inline edge controls + modal share one state)
    const [columnVisibility, setColumnVisibility] = useState(() => loadColumnVisibility(localStorage.getItem('userEmail')));
    const [showColumnVisibilityModal, setShowColumnVisibilityModal] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(QUOTES_COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
        } catch (e) { /* ignore storage write errors */ }
    }, [columnOrder]);

    const handleColumnDragStart = useCallback((e, id) => {
        setDraggedColumnId(id);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', id); } catch (err) { /* noop */ }
        }
    }, []);

    const handleColumnDragOver = useCallback((e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleColumnDrop = useCallback((e, targetId) => {
        e.preventDefault();
        const sourceId = draggedColumnId || (e.dataTransfer && e.dataTransfer.getData('text/plain'));
        if (!sourceId || sourceId === targetId) {
            setDraggedColumnId(null);
            return;
        }
        setColumnOrder((prev) => {
            const next = [...prev];
            const from = next.indexOf(sourceId);
            const to = next.indexOf(targetId);
            if (from === -1 || to === -1) return prev;
            next.splice(from, 1);
            next.splice(to, 0, sourceId);
            return next;
        });
        setDraggedColumnId(null);
    }, [draggedColumnId]);

    const handleColumnDragEnd = useCallback(() => setDraggedColumnId(null), []);

    const resetColumnOrder = useCallback(() => {
        const nextOrder = activeGroupId && activeGroupId !== NORMAL_GROUP_ID
            ? buildEffectiveColumnOrder([...DEFAULT_QUOTES_COLUMN_ORDER], activeGroupId, customGroups)
            : [...DEFAULT_QUOTES_COLUMN_ORDER];
        setColumnOrder(nextOrder);
        try { localStorage.removeItem(QUOTES_COLUMN_ORDER_STORAGE_KEY); } catch (e) { /* ignore */ }
    }, [activeGroupId, customGroups]);

    const quotesColumnDragHandlers = useMemo(() => ({
        onDragStart: handleColumnDragStart,
        onDragOver: handleColumnDragOver,
        onDrop: handleColumnDrop,
        onDragEnd: handleColumnDragEnd,
    }), [handleColumnDragStart, handleColumnDragOver, handleColumnDrop, handleColumnDragEnd]);

    const handleColumnVisibilityChange = useCallback((newVisibility) => {
        const next = newVisibility || {};
        setColumnVisibility(next);
        saveColumnVisibility(localStorage.getItem('userEmail'), next);
    }, []);

    const toggleColumnVisibility = useCallback((columnId) => {
        setColumnVisibility((prev) => {
            const currentlyVisible = prev[columnId] !== false;
            if (currentlyVisible) {
                const visibleIds = effectiveColumnOrder.filter((id) => prev[id] !== false);
                if (visibleIds.length <= 1) return prev;
            }
            const next = { ...prev, [columnId]: currentlyVisible ? false : true };
            saveColumnVisibility(localStorage.getItem('userEmail'), next);
            return next;
        });
    }, [effectiveColumnOrder]);

    const handleShowAllColumns = useCallback(() => {
        handleColumnVisibilityChange({});
    }, [handleColumnVisibilityChange]);

    const getColumnLabel = useCallback((id) => {
        if (COLUMN_LABELS[id]) return COLUMN_LABELS[id];
        if (isMergedColumnId(id)) {
            const group = customGroups.find((g) => getMergedIdForGroup(g.id) === id);
            return group?.name || 'Group';
        }
        return id;
    }, [customGroups]);

    const columnVisibilityList = useMemo(
        () => effectiveColumnOrder.map((id) => ({ key: id, label: getColumnLabel(id) })),
        [effectiveColumnOrder, getColumnLabel]
    );

    const quotesTableWidth = useMemo(
        () => getQuotesTableWidth(effectiveColumnOrder, columnVisibility),
        [effectiveColumnOrder, columnVisibility]
    );

    const hiddenColumnCount = useMemo(
        () => effectiveColumnOrder.filter((id) => columnVisibility[id] === false).length,
        [effectiveColumnOrder, columnVisibility]
    );

    useEffect(() => {
        saveColumnGroupsState(localStorage.getItem('userEmail'), columnGroupsState);
    }, [columnGroupsState]);

    const handleColumnGroupsApply = ({ activeGroupId: nextActiveId, defaultGroupId: nextDefaultId }) => {
        setColumnGroupsState((prev) => {
            setColumnOrder((prevOrder) => {
                const baseOrder = sanitizeColumnOrder(prevOrder.filter((id) => !isMergedColumnId(id)));
                if (!nextActiveId || nextActiveId === NORMAL_GROUP_ID) {
                    const prevMerged = getMergedIdForGroup(prev.activeGroupId);
                    const prevSources = getSourceIdsForGroup(prev.activeGroupId, prev.customGroups);
                    if (prevMerged && prevSources.length) {
                        return expandMergedColumnOrder(prevOrder, prevMerged, prevSources);
                    }
                    return baseOrder;
                }
                return buildEffectiveColumnOrder(baseOrder, nextActiveId, prev.customGroups);
            });
            return {
                ...prev,
                activeGroupId: nextActiveId,
                defaultGroupId: nextDefaultId,
            };
        });
    };

    const handleSaveCustomGroup = (group) => {
        const safeColumnIds = (group.columnIds || []).filter((id) => GROUPABLE_COLUMN_IDS.includes(id));
        setColumnGroupsState((prev) => {
            const payload = { ...group, columnIds: safeColumnIds };
            const existing = prev.customGroups.findIndex((g) => g.id === payload.id);
            const nextGroups = [...prev.customGroups];
            if (existing >= 0) nextGroups[existing] = payload;
            else nextGroups.push(payload);
            return { ...prev, customGroups: nextGroups };
        });
    };

    const handleDeleteCustomGroup = (groupId) => {
        setColumnGroupsState((prev) => {
            const nextActive = prev.activeGroupId === groupId ? NORMAL_GROUP_ID : prev.activeGroupId;
            const nextDefault = prev.defaultGroupId === groupId ? NORMAL_GROUP_ID : prev.defaultGroupId;
            return {
                ...prev,
                customGroups: prev.customGroups.filter((g) => g.id !== groupId),
                activeGroupId: nextActive,
                defaultGroupId: nextDefault,
            };
        });
        if (activeGroupId === groupId) {
            setColumnOrder((prev) => {
                const mergedId = getMergedIdForGroup(groupId);
                const sources = getSourceIdsForGroup(groupId, customGroups);
                return expandMergedColumnOrder(prev, mergedId, sources);
            });
        }
    };

    const getApiSearchTerm = () => {
        const top = String(searchTerm || '').trim();
        if (top) return top;
        const quoteId = String(filterQuoteId || '').trim();
        if (quoteId) return quoteId;
        const customer = String(filterCustomer || '').trim();
        if (customer) return customer;
        return '';
    };

    const hasTopSearch = () => Boolean(String(searchTerm || '').trim());

    const hasActiveFilters = () =>
        Boolean(String(filterQuoteId || '').trim())
        || Boolean(String(filterCustomer || '').trim())
        || Boolean(String(filterStatus || '').trim())
        || Boolean(String(filterOwner || '').trim());

    const isTimeInStageDefaultView = () =>
        itemsPerPage === TIME_IN_STAGE_ONE_PAGE_SIZE && !hasTopSearch() && !hasActiveFilters();

    const fetchQuotesData = async (index, maxRecords) => {
        const fetchGeneration = ++quotesFetchGenerationRef.current;
        try {
            setIsLoading(true);
            
            const response = await fetchQuotes(index, maxRecords, getApiSearchTerm() || searchTerm); 
            console.log('API Response:', response);
            if (fetchGeneration !== quotesFetchGenerationRef.current) return;
            
            if (response) {
                if (exports === true) {
                    setExports(false);
                    const headers = [
                        ["Quote", "Owner", "Customer Name", "Status", "Created On", "Time in Stage", "Lines #", "Shipping Comments", "Shipping Date", "Quote Total"]
                    ];

                    let data = response.content || [];
                    const rows = data.map((quote) => {
                        const firstLine = quote?.lines?.length > 0 ? quote.lines[0] : null;
                        
                        return [
                            quote.id,
                            firstLine?.createdBy?.username || '-',
                            quote.customer_name || '-',
                            quote.status || '-',
                            firstLine?.created_on ? firstLine.created_on.split(' ')[0] : '-',
                            formatTimeInStageLabel(getQuoteStageDateTime(quote)),
                            quote?.lines?.length || 0,
                            firstLine?.order_comment2 ? firstLine.order_comment2.replace(/<br>/gi, ' ') : '-',
                            getPriorityShippingDate(quote),
                            firstLine?.request_resale != null ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            }).format(Number(firstLine.request_resale)) : '-'
                        ];
                    });

                    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows]);
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotes");
                    XLSX.writeFile(workbook, "quotes.xlsx");
                } else {
                    setQuotesData(response.content || []);
                    setTotalRecords(response.totalElements || response.totalRecords || 0);
                }
            }
        } catch (error) {
            console.error('Error fetching quotes:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('jwt');
                navigate('/login');
            }
        } finally {
            if (fetchGeneration === quotesFetchGenerationRef.current) {
                setIsLoading(false);
            }
        }
    };

    // Fast path for the default 50-row view: walk from the newest API pages until
    // Time in Stage is older than 2, so both 1s and 2s are collected without loading everything.
    const fetchTimeInStageOneQuotes = async (showLoading = true) => {
        const fetchGeneration = ++quotesFetchGenerationRef.current;
        const minDaysOnPage = (content) => {
            const days = (content || []).map(getQuoteDaysInStage).filter((d) => d > 0);
            return days.length ? Math.min(...days) : Number.POSITIVE_INFINITY;
        };

        try {
            if (showLoading) setIsLoading(true);
            const pageSize = TIME_IN_STAGE_ONE_PAGE_SIZE;
            const first = await fetchQuotes(0, pageSize, searchTerm);
            if (fetchGeneration !== quotesFetchGenerationRef.current) return;

            const firstContent = first?.content || [];
            const total = first?.totalElements || first?.totalRecords || firstContent.length;
            const lastIndex = Math.max(0, Math.ceil(total / pageSize) - 1);

            let last = first;
            if (lastIndex > 0) {
                last = await fetchQuotes(lastIndex, pageSize, searchTerm);
                if (fetchGeneration !== quotesFetchGenerationRef.current) return;
            }

            const newestIsLast = lastIndex > 0 && minDaysOnPage(last?.content) <= minDaysOnPage(firstContent);
            const step = newestIsLast ? -1 : 1;
            const startIndex = newestIsLast ? lastIndex : 0;

            const collected = [];
            const seen = new Set();
            let pagesScanned = 0;
            let hitOlderThanTwo = false;
            let cursor = startIndex;

            while (cursor >= 0 && cursor <= lastIndex && pagesScanned < TIME_IN_STAGE_ONE_MAX_PAGES && !hitOlderThanTwo) {
                const batchIndexes = [];
                for (let n = 0; n < TIME_IN_STAGE_SCAN_BATCH && cursor >= 0 && cursor <= lastIndex; n += 1, cursor += step) {
                    batchIndexes.push(cursor);
                }

                const batch = await Promise.all(batchIndexes.map((i) => {
                    if (i === 0) return Promise.resolve(first);
                    if (i === lastIndex) return Promise.resolve(last);
                    return fetchQuotes(i, pageSize, searchTerm);
                }));
                if (fetchGeneration !== quotesFetchGenerationRef.current) return;

                for (const response of batch) {
                    pagesScanned += 1;
                    let foundRecent = false;
                    let foundOlder = false;
                    (response?.content || []).forEach((quote) => {
                        if (seen.has(quote.id)) return;
                        seen.add(quote.id);
                        const days = getQuoteDaysInStage(quote);
                        if (TIME_IN_STAGE_DEFAULT_DAYS.has(days)) {
                            collected.push(quote);
                            foundRecent = true;
                        } else if (days > 2) {
                            foundOlder = true;
                        }
                    });
                    if (foundOlder && (foundRecent || collected.length > 0)) {
                        hitOlderThanTwo = true;
                    }
                }
            }

            setQuotesData(collected);
            setTotalRecords(collected.length);
        } catch (error) {
            console.error('Error fetching Time in Stage 1-2 quotes:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('jwt');
                navigate('/login');
            }
        } finally {
            if (showLoading && fetchGeneration === quotesFetchGenerationRef.current) {
                setIsLoading(false);
            }
        }
    };

    const loadQuotesForCurrentView = async (pageIndex = currentPage - 1) => {
        if (isTimeInStageDefaultView()) {
            await fetchTimeInStageOneQuotes();
            return;
        }
        const maxRecords = itemsPerPage === 0 ? 10000 : itemsPerPage;
        await fetchQuotesData(pageIndex, maxRecords);
    };

    const SyncAndfetchQuotesData = async (user) => {
        try {
            await syncQuotes(user);
            await loadQuotesForCurrentView();
        } catch (error) {
            console.error('Error syncing quotes:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('jwt');
                navigate('/login');
            }
        }
    };

    // Page size 50 with no search/filters: Time in Stage 1–2 quotes (client paginates).
    // With search: query all quotes so older-day records can appear.
    useEffect(() => {
        if (itemsPerPage === TIME_IN_STAGE_ONE_PAGE_SIZE && !hasTopSearch() && !hasActiveFilters()) {
            setCurrentPage(1);
            fetchTimeInStageOneQuotes();
        }
    }, [itemsPerPage, searchTerm]);

    useEffect(() => {
        if (itemsPerPage === TIME_IN_STAGE_ONE_PAGE_SIZE && !hasTopSearch() && !hasActiveFilters()) return;
        const maxRecords = itemsPerPage === 0 ? 10000 : itemsPerPage;
        fetchQuotesData(currentPage - 1, maxRecords);
    }, [currentPage, itemsPerPage, searchTerm]);

    // Read back quote-level status tags: prefer manualUpdateStatus, else fall back to
    // scanning quote.comment[] for entries carrying a `status` field (same pattern Orders uses).
    useEffect(() => {
        if (!Array.isArray(quotesData)) return;
        quotesData.forEach((quote) => {
            if (quote?.manualUpdateStatus) {
                try {
                    const tags = JSON.parse(quote.manualUpdateStatus);
                    setSubmittedQuoteStatuses((prev) => ({ ...prev, [quote.id]: tags }));
                } catch (e) {
                    console.error("Invalid JSON in manualUpdateStatus:", e);
                }
            } else if (Array.isArray(quote?.comment)) {
                const statusComments = quote.comment
                    .filter((c) => c.status)
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                if (statusComments.length > 0) {
                    try {
                        const tags = JSON.parse(statusComments[0].status);
                        if (Array.isArray(tags)) {
                            setSubmittedQuoteStatuses((prev) => ({ ...prev, [quote.id]: tags }));
                        }
                    } catch (e) {
                        console.error("Invalid JSON in comment.status:", e);
                    }
                }
            }

            if (Array.isArray(quote?.quoteUrls)) {
                // Merge instead of overwrite: keep any optimistic local-only links (just added,
                // not yet reflected in this fetch) so a stale/racing refresh can't make them
                // flash and disappear before the backend catches up.
                setQuoteAccessLinks((prev) => {
                    const serverLinks = quote.quoteUrls;
                    const localOnly = (prev[quote.id] || []).filter(
                        (local) => !serverLinks.some((s) => s.url === local.url && s.urlName === local.urlName)
                    );
                    return { ...prev, [quote.id]: [...serverLinks, ...localOnly] };
                });
            }

            (quote?.lines || []).forEach((line) => {
                if (line?.lineItemStatus) {
                    try {
                        const tags = JSON.parse(line.lineItemStatus);
                        setSubmittedQuoteLineStatuses((prev) => ({ ...prev, [line.id]: tags }));
                    } catch (e) {
                        console.error("Invalid JSON in lineItemStatus:", e);
                    }
                }
            });
        });
    }, [quotesData]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    // Helper function to get owner name from lines (must be before sortQuotesByColumn)
    const getQuoteOwner = (quote) => {
        if (!quote?.lines?.length) return '-';
        const lineWithOwner = quote.lines.find(line => line.createdBy?.username);
        return lineWithOwner?.createdBy?.username || '-';
    };

    // Helper function to get earliest created date (must be before sortQuotesByColumn)
    const getEarliestCreatedDate = (quote) => {
        if (!quote?.lines?.length) return '-';
        const dates = quote.lines
            .map(line => line.created_on)
            .filter(date => date)
            .sort();
        return dates.length > 0 ? dates[0].split(' ')[0] : '-';
    };

    // Numeric days in stage for sorting (handles 30+ as actual day count so 31, 32... sort correctly)
    const getDaysInStageNumeric = (quote) => getQuoteDaysInStage(quote);

    // Sort quotes by column (ascending/descending)
    const sortQuotesByColumn = (quotes, sortKey, sortDirection) => {
        if (!quotes || !Array.isArray(quotes) || !sortKey || !sortDirection) return quotes;
        return [...quotes].sort((a, b) => {
            let valueA, valueB;
            switch (sortKey) {
                case 'quote':
                    valueA = parseInt(a.id || 0);
                    valueB = parseInt(b.id || 0);
                    return sortDirection === 'ascending' ? valueA - valueB : valueB - valueA;
                case 'owner':
                    valueA = (getQuoteOwner(a) || '').toLowerCase();
                    valueB = (getQuoteOwner(b) || '').toLowerCase();
                    return sortDirection === 'ascending' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                case 'customer_name':
                    valueA = (a.customer_name || '').toLowerCase();
                    valueB = (b.customer_name || '').toLowerCase();
                    return sortDirection === 'ascending' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                case 'status':
                    valueA = (a.status || '').toLowerCase();
                    valueB = (b.status || '').toLowerCase();
                    return sortDirection === 'ascending' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                case 'created_on': {
                    valueA = getEarliestCreatedDate(a) || '';
                    valueB = getEarliestCreatedDate(b) || '';
                    const dateCmp = sortDirection === 'ascending'
                        ? valueA.localeCompare(valueB)
                        : valueB.localeCompare(valueA);
                    if (dateCmp !== 0) return dateCmp;
                    return parseInt(b.id || 0, 10) - parseInt(a.id || 0, 10);
                }
                case 'time_in_stage':
                    valueA = getDaysInStageNumeric(a);
                    valueB = getDaysInStageNumeric(b);
                    return sortDirection === 'ascending' ? valueA - valueB : valueB - valueA;
                case 'lines':
                    valueA = a?.lines?.length || 0;
                    valueB = b?.lines?.length || 0;
                    return sortDirection === 'ascending' ? valueA - valueB : valueB - valueA;
                default:
                    return 0;
            }
        });
    };

    const handleColumnSort = (key) => {
        setColumnSort(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'ascending' ? 'descending' : 'ascending' };
            }
            const initialDirection = (key === 'quote' || key === 'created_on') ? 'descending' : 'ascending';
            return { key, direction: initialDirection };
        });
    };

    const getColumnSortIcon = (key) => {
        if (columnSort.key !== key) return null;
        return columnSort.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    const applyFilters = () => {
        setCurrentPage(1);
        loadQuotesForCurrentView(0);
    };

    // Simple filtering based on search term and filter inputs
    const getFilteredQuotes = () => {
        // Exclude quotes with no Created On (those appear in Quotes Archive only)
        let filtered = quotesData.filter(quote => getEarliestCreatedDate(quote) !== '-');
        
        // Apply search term filter
        if (searchTerm) {
            filtered = filtered.filter(quote => 
                quote.id.toString().includes(searchTerm) ||
                quote.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                quote.status?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Apply additional filters if they exist
        if (filterQuoteId) {
            filtered = filtered.filter(quote => quote.id.toString().includes(filterQuoteId));
        }
        if (filterCustomer) {
            filtered = filtered.filter(quote => 
                quote.customer_name?.toLowerCase().includes(filterCustomer.toLowerCase())
            );
        }
        if (filterStatus) {
            filtered = filtered.filter(quote => quote.status === filterStatus);
        }
        if (filterOwner) {
            filtered = filtered.filter(quote => 
                quote.lines?.some(line => line.createdBy?.username?.toLowerCase().includes(filterOwner.toLowerCase()))
            );
        }

        if (isTimeInStageDefaultView()) {
            filtered = filtered.filter(quote => matchesDefaultTimeInStage(quote));
        }
        
        return filtered;
    };

    const filteredQuotes = getFilteredQuotes();

    const getCurrentPageData = () => {
        let rows = filteredQuotes;
        if (columnSort.key && columnSort.direction) {
            rows = sortQuotesByColumn(filteredQuotes, columnSort.key, columnSort.direction);
        }
        if (isTimeInStageDefaultView()) {
            const start = (currentPage - 1) * itemsPerPage;
            return rows.slice(start, start + itemsPerPage);
        }
        return rows;
    };

    const currentPageData = getCurrentPageData();

    // Sticky Notes column: permanent quote notes, keyed by quote number.
    // A save is relayed to everyone else on this page over presence.
    const stickyNoteQuoteKey = (() => {
        const nums = new Set();
        (currentPageData || []).forEach((quote) => {
            if (quote?.id != null && quote.id !== '') nums.add(String(quote.id));
        });
        return Array.from(nums).sort().join('|');
    })();
    const stickyNoteQuoteNums = useMemo(
        () => (stickyNoteQuoteKey ? stickyNoteQuoteKey.split('|') : []),
        [stickyNoteQuoteKey]
    );
    const stickyNotes = useStickyNotes(stickyNoteQuoteNums, {
        onChange: (quoteNumber, notes) => presence.broadcastSave({
            cell: cellKey(PRESENCE_PAGE, 'order', quoteNumber, 'stickyNotes'),
            kind: 'stickyNotes',
            lineId: String(quoteNumber),
            value: notes,
        }),
    });
    const applyRemoteStickyNotes = stickyNotes.applyRemote;

    // Permanent instructions for the quote's customer. Only the ship-to
    // instruction is shown: the line's shipto_id when it matches, otherwise
    // the customer's ship-to addresses.
    const shippingInstructions = useShippingInstructions(currentPageData);

    const paginationTotal = isTimeInStageDefaultView() ? filteredQuotes.length : totalRecords;
    const totalPages = itemsPerPage === 0 ? 1 : Math.max(1, Math.ceil(paginationTotal / itemsPerPage));
    const paginationStart = currentPageData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const paginationEnd = currentPageData.length === 0 ? 0 : paginationStart + currentPageData.length - 1;
    // Page size 50 still loads Time in Stage 1–2 quotes; if fewer than 50 match, show the selected page size in "of".
    const paginationOf =
        isTimeInStageDefaultView() && paginationTotal > 0 && paginationTotal < itemsPerPage
            ? itemsPerPage
            : paginationTotal;

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]); 

    const handleViewQuoteDetails = (quote) => {
        setSelectedQuote(quote);
        setShowModal(true);
    };

    const handleViewQuoteInfo = async (quote) => {
        try {
            setModalLoading(true);
            console.log('Fetching details for quote:', quote.id);
            
            const response = await fetchQuotes(0, 10, "", quote.id);
            console.log('Quote details response:', response);
            
            if (response) {
                if (response.content && response.content.length > 0) {
                    const quoteWithDetails = response.content.find(q => q.id === quote.id);
                    if (quoteWithDetails) {
                        setSelectedQuoteDetails(quoteWithDetails);
                    } else {
                        setSelectedQuoteDetails(quote);
                    }
                } else if (response.id === quote.id) {
                    setSelectedQuoteDetails(response);
                } else if (response.data && response.data.id === quote.id) {
                    setSelectedQuoteDetails(response.data);
                } else {
                    setSelectedQuoteDetails(quote);
                }
            } else {
                setSelectedQuoteDetails(quote);
            }
            
            setShowDetailsModal(true);
            
        } catch (error) {
            console.error('Error fetching quote details:', error);
            setSelectedQuoteDetails(quote);
            setShowDetailsModal(true);
        } finally {
            setModalLoading(false);
        }
    };

    const toggleInlineQuoteDetails = (quote) => {
        const quoteId = quote?.id;
        if (!quoteId) return;

        setExpandedInlineQuotes(prev => ({
            ...prev,
            [quoteId]: !prev[quoteId]
        }));
    };

    const handleEyeIconClick = async (quote) => {
        if (inlineDropdownMode) {
            toggleInlineQuoteDetails(quote);
            return;
        }

        await handleViewQuoteInfo(quote);
    };

    const handleInlinePartDropdownToggle = (line, event) => {
        event.stopPropagation();
        const partId = line?.part?.id;
        if (!partId) return;

        const buttonRect = event.currentTarget.getBoundingClientRect();
        const isSamePartOpen = inlinePartDropdown.open && inlinePartDropdown.partId === partId;

        if (isSamePartOpen) {
            setInlinePartDropdown({ open: false, top: 0, left: 0, partId: null });
            return;
        }

        const menuHeight = 140;
        const menuWidth = 200;
        const spaceBelow = window.innerHeight - buttonRect.bottom;

        setInlinePartDropdown({
            open: true,
            top: spaceBelow < menuHeight
                ? Math.max(8, buttonRect.bottom - menuHeight)
                : buttonRect.top,
            left: Math.min(buttonRect.right + 8, window.innerWidth - menuWidth - 8),
            partId
        });
    };

    useEffect(() => {
        if (!inlinePartDropdown.open) return;

        const closeDropdown = (event) => {
            const target = event?.target;
            if (target?.closest?.('.inline-part-link-trigger') || target?.closest?.('.part-link-dropdown-menu')) {
                return;
            }
            setInlinePartDropdown({ open: false, top: 0, left: 0, partId: null });
        };

        document.addEventListener('mousedown', closeDropdown);
        window.addEventListener('scroll', closeDropdown, true);
        window.addEventListener('resize', closeDropdown);

        return () => {
            document.removeEventListener('mousedown', closeDropdown);
            window.removeEventListener('scroll', closeDropdown, true);
            window.removeEventListener('resize', closeDropdown);
        };
    }, [inlinePartDropdown.open]);

    // Helper function to sort comments by date/time in descending order (most recent first)
    const sortCommentsByDate = (comments) => {
        if (!Array.isArray(comments) || comments.length === 0) return comments;
        return [...comments].sort((a, b) => {
            const dateA = a.dateStr && a.timeStr ? new Date(`${a.dateStr} ${a.timeStr}`) :
                a.createdAt ? new Date(a.createdAt) :
                    a.date ? new Date(a.date) : new Date(0);
            const dateB = b.dateStr && b.timeStr ? new Date(`${b.dateStr} ${b.timeStr}`) :
                b.createdAt ? new Date(b.createdAt) :
                    b.date ? new Date(b.date) : new Date(0);
            return dateB - dateA;
        });
    };

    // Comment handlers — keep commentsMap, quotesData, and the open modal in sync
    const patchQuoteCommentList = (quoteId, updater) => {
        setQuotesData(prev => {
            if (!Array.isArray(prev)) return prev;
            return prev.map(q => {
                if (String(q.id) !== String(quoteId)) return q;
                const current = Array.isArray(q.comment) ? q.comment : [];
                return { ...q, comment: updater(current) };
            });
        });
        setSelectedQuoteDetails(prev => {
            if (!prev || String(prev.id) !== String(quoteId)) return prev;
            const current = Array.isArray(prev.comment) ? prev.comment : [];
            return { ...prev, comment: updater(current) };
        });
        setSelectedQuote(prev => {
            if (!prev || String(prev.id) !== String(quoteId)) return prev;
            const current = Array.isArray(prev.comment) ? prev.comment : [];
            return { ...prev, comment: updater(current) };
        });
    };

    const handleDeleteComment = async (quoteId, commentId) => {
        const currentComments = [
            ...((commentsMap[quoteId] || [])),
            ...(Array.isArray(quotesData) ? (quotesData.find((q) => String(q.id) === String(quoteId))?.comment || []) : []),
            ...(selectedQuoteDetails && String(selectedQuoteDetails.id) === String(quoteId) ? (selectedQuoteDetails.comment || []) : []),
        ];
        const target = currentComments.find((c) => {
            const id = getCommentId(c);
            return String(id) === String(commentId) || String(c?.id) === String(commentId) || String(c?.commentId) === String(commentId);
        });
        const backendId = getCommentId(target) || commentId;
        const isSameComment = (comment) => {
            const id = getCommentId(comment);
            return String(id) === String(backendId)
                || String(id) === String(commentId)
                || String(comment?.id) === String(commentId)
                || String(comment?.commentId) === String(commentId);
        };

        setCommentsMap(prev => {
            const newMap = { ...prev };
            if (newMap[quoteId]) {
                newMap[quoteId] = newMap[quoteId].filter(comment => !isSameComment(comment));
            }
            return newMap;
        });
        patchQuoteCommentList(quoteId, comments => comments.filter(c => !isSameComment(c)));

        try {
            if (!isTempCommentId(backendId)) {
                await deleteQuoteComment(backendId, quoteId);
            }
        } catch (error) {
            console.error('Error deleting quote comment from backend:', error);
        }
    };

    const handleResolveThread = (quoteId) => {
        setCommentsMap(prev => {
            const newMap = { ...prev };
            if (newMap[quoteId]) {
                newMap[quoteId] = newMap[quoteId].map(comment => ({
                    ...comment,
                    status: 'resolved',
                    resolved: true
                }));
            }
            return newMap;
        });
        patchQuoteCommentList(quoteId, comments => comments.map(comment => ({
            ...comment,
            status: 'resolved',
            resolved: true
        })));
    };

    const handleAddComment = (quoteId, newComment, status = 'pending') => {
        const currentUser = localStorage.getItem('userEmail') || localStorage.getItem('userName') || 'Current User';
        const commentToStore = toStoredComment(newComment, status, { lastEditedBy: currentUser });

        applyCommentPatch(quoteId, commentToStore);

        const quoteRecord = (Array.isArray(quotesData) ? quotesData.find((q) => String(q.id) === String(quoteId)) : null)
            || (selectedQuoteDetails && String(selectedQuoteDetails.id) === String(quoteId) ? selectedQuoteDetails : null);

        // Everyone else gets the note without a refresh, and a minimized blue chat
        // pops on any page the same way an order comment does.
        presence.broadcastSave({
            cell: quoteNotesCell(quoteId),
            kind: 'comment',
            quoteId: String(quoteId),
            customerName: quoteRecord?.customer_name || '',
            value: commentToStore,
        });
    };

    // --- Quote Status handlers ---
    const handleQuoteStatusTriggerClick = (event, quote) => {
        if (quoteLineStatusDropdownOpenId) setQuoteLineStatusDropdownOpenId(null);
        if (quoteStatusDropdownOpenId === quote.id) {
            setQuoteStatusDropdownOpenId(null);
            return;
        }

        // Someone else is already in this quote status - don't open a second picker.
        const statusLockedBy = quoteStatusLockOwner(quote.id);
        if (statusLockedBy) {
            alert(`${statusLockedBy.name} is changing this quote status. Try again in a moment.`);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        setQuoteStatusDropdownPosition(orderStatusMenuPosition(rect));
        setQuoteStatusDropdownOpenId(quote.id);
    };

    const handleQuoteStatusToggle = (quoteId, tag) => {
        setQuoteStatusSelections(prev => {
            const current = prev[quoteId] || [];
            const isSelected = current.some(t => t.label === tag.label);
            return {
                ...prev,
                [quoteId]: isSelected
                    ? current.filter(t => t.label !== tag.label)
                    : [...current, tag],
            };
        });
    };

    const handleQuoteTagDeselect = (quoteId, tag) => {
        setQuoteTagsToDeselect(prev => {
            const current = prev[quoteId] || [];
            const isMarked = current.some(t => t.label === tag.label);
            return {
                ...prev,
                [quoteId]: isMarked
                    ? current.filter(t => t.label !== tag.label)
                    : [...current, tag],
            };
        });
    };

    const hasQuoteStatusChangesToSubmit = (quoteId) => {
        return (quoteStatusSelections[quoteId] || []).length > 0
            || (quoteTagsToDeselect[quoteId] || []).length > 0;
    };

    const handleQuoteStatusSubmit = async (quoteId) => {
        const statusLockedBy = quoteStatusLockOwner(quoteId);
        if (statusLockedBy) {
            alert(`${statusLockedBy.name} is changing this quote status. Try again in a moment.`);
            return;
        }

        const currentSelections = quoteStatusSelections[quoteId] || [];
        const submittedSelections = submittedQuoteStatuses[quoteId] || [];
        const tagsToRemove = quoteTagsToDeselect[quoteId] || [];

        let newSubmitted = Array.isArray(submittedSelections) ? [...submittedSelections] : [];

        if (tagsToRemove.length > 0) {
            newSubmitted = newSubmitted.filter(
                tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
            );
        }

        if (currentSelections.length > 0) {
            currentSelections.forEach(sel => {
                if (!newSubmitted.some(t => t.label === sel.label)) {
                    newSubmitted.push(sel);
                }
            });
        }

        // Reuse saveQuoteComment: status-only payload (no comment text), same pattern
        // Orders uses to piggyback order status onto saveComment.
        await saveQuoteComment({ status: JSON.stringify(newSubmitted), quoteId });

        setQuoteStatusDropdownOpenId(null);
        applyQuoteStatusPatch(quoteId, newSubmitted);

        // Let everyone else on this page patch the same cell without a refresh.
        presence.broadcastSave({
            cell: quoteStatusCell(quoteId),
            kind: 'quoteStatus',
            quoteId: String(quoteId),
            value: newSubmitted,
        });
    };

    // --- Quote Line Status handlers ---
    const toggleQuoteLineStatusDropdown = (event, lineId) => {
        if (quoteStatusDropdownOpenId) setQuoteStatusDropdownOpenId(null);
        if (quoteLineStatusDropdownOpenId === lineId) {
            setQuoteLineStatusDropdownOpenId(null);
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        setQuoteLineStatusDropdownPosition(orderStatusMenuPosition(rect));
        setQuoteLineStatusDropdownOpenId(lineId);
    };

    // The tag menu is position:fixed. Close it when the page or table scrolls,
    // and when the pointer lands outside the menu, so it cannot stay behind.
    // Scrolling inside the list, or pressing the field that opened it, keeps it open.
    useEffect(() => {
        if (!quoteStatusDropdownOpenId && !quoteLineStatusDropdownOpenId) return undefined;

        const closeMenus = () => {
            setQuoteStatusDropdownOpenId(null);
            setQuoteLineStatusDropdownOpenId(null);
        };

        const handleScroll = (event) => {
            const target = event?.target;
            if (target instanceof Element && target.closest('.order-status-dropdown')) return;
            closeMenus();
        };

        const handlePointerDown = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                closeMenus();
                return;
            }
            if (
                target.closest('.order-status-dropdown')
                || target.closest('.quote-status-trigger')
                || target.closest('.quote-line-status-trigger')
            ) {
                return;
            }
            closeMenus();
        };

        window.addEventListener('scroll', handleScroll, true);
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [quoteStatusDropdownOpenId, quoteLineStatusDropdownOpenId]);

    const handleQuoteLineStatusToggle = (lineId, tag) => {
        setQuoteLineStatusSelections(prev => {
            const current = prev[lineId] || [];
            const isSelected = current.some(t => t.label === tag.label);
            return {
                ...prev,
                [lineId]: isSelected
                    ? current.filter(t => t.label !== tag.label)
                    : [...current, tag],
            };
        });
    };

    const handleQuoteLineTagDeselect = (lineId, tag) => {
        setQuoteLineTagsToDeselect(prev => {
            const current = prev[lineId] || [];
            const isMarked = current.some(t => t.label === tag.label);
            return {
                ...prev,
                [lineId]: isMarked
                    ? current.filter(t => t.label !== tag.label)
                    : [...current, tag],
            };
        });
    };

    const hasQuoteLineChangesToSubmit = (lineId) => {
        return (quoteLineStatusSelections[lineId] || []).length > 0
            || (quoteLineTagsToDeselect[lineId] || []).length > 0;
    };

    // Quotes this user currently has open: expanded inline, or in the details modal.
    // Announced so the grid can show "X is also on this quote".
    const openRowIds = useMemo(() => {
        const ids = Object.keys(expandedInlineQuotes).filter((id) => expandedInlineQuotes[id]);
        const modalQuote = showDetailsModal ? selectedQuoteDetails?.id : null;
        if (modalQuote && !ids.includes(String(modalQuote))) ids.push(String(modalQuote));
        return ids;
    }, [expandedInlineQuotes, showDetailsModal, selectedQuoteDetails]);

    useRowPresence(PRESENCE_PAGE, openRowIds, presence);

    const quoteRowCell = useCallback(
        (quoteId) => cellKey(PRESENCE_PAGE, 'order', quoteId, 'row'),
        []
    );

    /** Everyone active anywhere on this row, shown next to the quote number.
     *  Editors of the Quote Status / Notes cells are included so their activity
     *  is visible without the quote being expanded - the cell badges alone are
     *  easy to miss in a wide grid. */
    const renderRowActivity = useCallback(
        (quoteId) => {
            if (!quoteId) return null;
            const users = mergeRowPresence([
                {
                    users: presenceUsersInCell(quoteStatusCell(quoteId)),
                    activity: 'changing this quote status',
                },
                {
                    users: presenceUsersInCell(quoteNotesCell(quoteId)),
                    activity: 'in the notes',
                },
                {
                    users: presenceUsersInCell(quoteRowCell(quoteId)),
                    activity: 'also on this quote',
                },
            ]);
            if (!users.length) return null;
            return <CellPresence users={users} size={16} compact />;
        },
        [presenceUsersInCell, quoteRowCell, quoteStatusCell, quoteNotesCell]
    );

    // Announce the open line-status dropdown as our focused cell and hold a soft
    // lock on it - covers every open/close path through quoteLineStatusDropdownOpenId.
    useEffect(() => {
        if (!quoteLineStatusDropdownOpenId) return undefined;
        const key = quoteLineCell(quoteLineStatusDropdownOpenId);
        presenceFocus(key);
        return () => presenceBlur(key);
    }, [quoteLineStatusDropdownOpenId, quoteLineCell, presenceFocus, presenceBlur]);

    // Quote Status: one focused cell covers every open/close path through
    // quoteStatusDropdownOpenId, so it shows on the collapsed row too.
    useFocusedCell(
        quoteStatusDropdownOpenId ? quoteStatusCell(quoteStatusDropdownOpenId) : null,
        Boolean(quoteStatusDropdownOpenId),
        presence,
        { lock: true }
    );

    // A live face shows only while that quote's chat window is open.
    const openQuoteChatKey = useMemo(() => {
        const ids = [];
        (commentDock?.threads || []).forEach((thread) => {
            if (thread.minimized || thread.quoteId == null || thread.quoteId === '') return;
            ids.push(String(thread.quoteId));
        });
        ids.sort();
        return ids.join('|');
    }, [commentDock?.threads]);

    useEffect(() => {
        const keys = (openQuoteChatKey ? openQuoteChatKey.split('|') : []).map((id) => quoteNotesCell(id));
        keys.forEach((key) => presenceFocus(key, { lock: false }));
        return () => {
            keys.forEach((key) => presenceBlur(key));
        };
    }, [openQuoteChatKey, quoteNotesCell, presenceFocus, presenceBlur]);

    /** Wire one row's notes popup to presence: claim that cell while it is open. */
    const handleNotesVisibility = useCallback((quoteId, isVisible) => {
        const id = String(quoteId);
        setOpenNotesQuoteIds((prev) => {
            if (isVisible) {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
            }
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    /** Push a committed quote line status into the grid state. Shared by the local
     *  submit path and by remote saves arriving over presence. */
    const applyQuoteLineStatusPatch = useCallback((lineId, newSubmitted) => {
        const lineJson = JSON.stringify(newSubmitted);

        setSubmittedQuoteLineStatuses(prev => ({ ...prev, [lineId]: newSubmitted }));
        setQuoteLineStatusSelections(prev => ({ ...prev, [lineId]: [] }));
        setQuoteLineTagsToDeselect(prev => ({ ...prev, [lineId]: [] }));

        setQuotesData(prevQuotes => {
            if (!Array.isArray(prevQuotes)) return prevQuotes;
            return prevQuotes.map(q => {
                if (!Array.isArray(q.lines) || !q.lines.some(l => String(l.id) === String(lineId))) return q;
                return {
                    ...q,
                    lines: q.lines.map(l => String(l.id) === String(lineId)
                        ? { ...l, lineItemStatus: lineJson }
                        : l),
                };
            });
        });
    }, []);

    /** Push a committed quote status into the grid state. Shared by the local
     *  submit path and by remote saves arriving over presence. */
    const applyQuoteStatusPatch = useCallback((quoteId, newSubmitted) => {
        const statusJson = JSON.stringify(newSubmitted);

        setSubmittedQuoteStatuses(prev => ({ ...prev, [quoteId]: newSubmitted }));
        setQuoteStatusSelections(prev => ({ ...prev, [quoteId]: [] }));
        setQuoteTagsToDeselect(prev => ({ ...prev, [quoteId]: [] }));

        setQuotesData(prevQuotes => {
            if (!Array.isArray(prevQuotes)) return prevQuotes;
            return prevQuotes.map(q => String(q.id) === String(quoteId)
                ? { ...q, manualUpdateStatus: statusJson }
                : q);
        });
    }, []);

    /** Merge one saved comment into commentsMap. Shared by the local add path and
     *  by remote adds arriving over presence. */
    const applyCommentPatch = useCallback((quoteId, commentToStore) => {
        if (!commentToStore) return;
        const storedId = getCommentId(commentToStore);
        setCommentsMap(prev => {
            const currentComments = prev[quoteId] || [];
            const filtered = currentComments.filter(c => String(getCommentId(c)) !== String(storedId));
            return {
                ...prev,
                [quoteId]: [...filtered, commentToStore]
            };
        });
        setQuotesData(prev => {
            if (!Array.isArray(prev)) return prev;
            return prev.map(q => {
                if (String(q.id) !== String(quoteId)) return q;
                const current = Array.isArray(q.comment) ? q.comment : [];
                const filtered = current.filter(c => String(getCommentId(c)) !== String(storedId));
                return { ...q, comment: [...filtered, commentToStore] };
            });
        });
        setSelectedQuoteDetails(prev => {
            if (!prev || String(prev.id) !== String(quoteId)) return prev;
            const current = Array.isArray(prev.comment) ? prev.comment : [];
            const filtered = current.filter(c => String(getCommentId(c)) !== String(storedId));
            return { ...prev, comment: [...filtered, commentToStore] };
        });
        setSelectedQuote(prev => {
            if (!prev || String(prev.id) !== String(quoteId)) return prev;
            const current = Array.isArray(prev.comment) ? prev.comment : [];
            const filtered = current.filter(c => String(getCommentId(c)) !== String(storedId));
            return { ...prev, comment: [...filtered, commentToStore] };
        });
    }, []);

    /** Someone else saved on this page - patch that one cell, no reload. */
    const applyRemoteSave = useCallback((evt) => {
        if (!evt) return;
        switch (evt.kind) {
            case 'quoteLineStatus':
                if (evt.lineId) applyQuoteLineStatusPatch(evt.lineId, evt.value || []);
                break;
            case 'quoteStatus':
                if (evt.quoteId) applyQuoteStatusPatch(evt.quoteId, evt.value || []);
                break;
            case 'comment':
                if (evt.quoteId) applyCommentPatch(evt.quoteId, evt.value);
                break;
            case 'stickyNotes':
                // lineId carries the quote number for sticky notes.
                applyRemoteStickyNotes(evt.lineId, evt.value || []);
                break;
            default:
                break;
        }
    }, [applyQuoteLineStatusPatch, applyQuoteStatusPatch, applyCommentPatch, applyRemoteStickyNotes]);

    const handleQuoteLineStatusSubmit = async (lineId) => {
        const lockedBy = quoteLineLockOwner(lineId);
        if (lockedBy) {
            alert(`${lockedBy.name} is editing this line status. Try again in a moment.`);
            return;
        }

        const currentSelections = quoteLineStatusSelections[lineId] || [];
        const submittedSelections = submittedQuoteLineStatuses[lineId] || [];
        const tagsToRemove = quoteLineTagsToDeselect[lineId] || [];

        let newSubmitted = Array.isArray(submittedSelections) ? [...submittedSelections] : [];

        if (tagsToRemove.length > 0) {
            newSubmitted = newSubmitted.filter(
                tag => !tagsToRemove.some(removeTag => removeTag.label === tag.label)
            );
        }

        if (currentSelections.length > 0) {
            currentSelections.forEach(sel => {
                if (!newSubmitted.some(t => t.label === sel.label)) {
                    newSubmitted.push(sel);
                }
            });
            setQuoteLineStatusSelections(prev => ({ ...prev, [lineId]: [] }));
        }

        await saveQuoteLineStatus({ quoteLineId: lineId, status: JSON.stringify(newSubmitted) });

        setQuoteLineStatusDropdownOpenId(null);
        applyQuoteLineStatusPatch(lineId, newSubmitted);

        // Let everyone else on this page patch the same cell without a refresh.
        presence.broadcastSave({
            cell: quoteLineCell(lineId),
            kind: 'quoteLineStatus',
            lineId: String(lineId),
            value: newSubmitted,
        });
    };

    // --- Quote Access (manage links) handlers ---
    const handleQuoteAccessClick = (quote) => {
        setSelectedQuoteForAccess(quote);
        setShowQuoteAccessModal(true);
        setNewQuoteLinkUrl('');
        setNewQuoteLinkUrlName('');
    };

    const handleAddQuoteAccessLink = async () => {
        if (!newQuoteLinkUrlName.trim()) {
            alert('Please enter a URL name');
            return;
        }
        if (!newQuoteLinkUrl.trim()) {
            alert('Please enter a URL');
            return;
        }
        try {
            new URL(newQuoteLinkUrl);
        } catch {
            alert('Please enter a valid URL (include http:// or https://)');
            return;
        }

        try {
            const quoteId = selectedQuoteForAccess.id;
            const urlName = newQuoteLinkUrlName.trim();
            const url = newQuoteLinkUrl.trim();
            const requestData = { action: 'add', quoteId, url, urlName };
            const response = await manageQuoteUrl(requestData);
            if (response) {
                setQuoteAccessLinks(prev => ({
                    ...prev,
                    [quoteId]: [...(prev[quoteId] || []), { id: Date.now(), url, urlName }],
                }));
                handleCloseQuoteAccessModal();
            } else {
                alert('Failed to save URL. Please try again.');
            }
        } catch (error) {
            console.error('Error saving quote URL:', error);
            alert('Error saving URL. Please try again.');
        }
    };

    const handleDeleteQuoteAccessLink = async (quoteId, linkId) => {
        try {
            const requestData = { action: 'delete', quoteId, id: linkId };
            const response = await manageQuoteUrl(requestData);
            if (response) {
                setQuoteAccessLinks(prev => ({
                    ...prev,
                    [quoteId]: (prev[quoteId] || []).filter(link => link.id !== linkId),
                }));
            } else {
                alert('Failed to delete URL. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting quote URL:', error);
            alert('Error deleting URL. Please try again.');
        }
    };

    const handleCloseQuoteAccessModal = () => {
        setShowQuoteAccessModal(false);
        setSelectedQuoteForAccess(null);
        setNewQuoteLinkUrl('');
        setNewQuoteLinkUrlName('');
    };

    const handleAutoRefresh = async () => {
        if (isTimeInStageDefaultView()) {
            await fetchTimeInStageOneQuotes(false);
            return;
        }
        const maxRecords = itemsPerPage === 0 ? 10000 : itemsPerPage;
        const response = await fetchQuotes(currentPage - 1, maxRecords, getApiSearchTerm() || searchTerm);
        if (response) {
            setQuotesData(response.content || []);
            setTotalRecords(response.totalElements || response.totalRecords || 0);
        }
    };

    const { timerFormatted, restartTimer } = useAutoRefreshTimer(handleAutoRefresh, AUTO_REFRESH_INTERVAL_SECONDS);

    const handleRefreshQuotes = async () => {
        setIsRefreshing(true);
        try {
            const userObj = {
                email: localStorage.getItem('userEmail'),
                userName: localStorage.getItem('userEmail'),
                name: localStorage.getItem('userName') || localStorage.getItem('userEmail') || "User"
            };
            await SyncAndfetchQuotesData(userObj);

            setLastRefreshData({
                timestamp: new Date(),
                user: localStorage.getItem('userEmail'),
                success: true
            });
            restartTimer();
        } catch (error) {
            console.error('Refresh failed:', error);
            setLastRefreshData({
                timestamp: new Date(),
                user: localStorage.getItem('userEmail'),
                success: false,
                error: error.message
            });
            throw error;
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleManualPageRefresh = async () => {
        try {
            await loadQuotesForCurrentView();
            restartTimer();
        } catch (error) {
            console.error('Manual refresh error:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('jwt');
                navigate('/login');
            }
            throw error;
        }
    };

    const syncQuotesPageTitle = useCallback(() => {
        const scrollEl = tableScrollRef.current;
        const titleEl = titleWrapRef.current;
        if (!scrollEl || !titleEl) return;

        const scrolled = scrollEl.scrollTop;
        if (scrolled > 24) {
            titleEl.classList.add('ov-title-collapsed');
        } else if (scrolled < 4) {
            titleEl.classList.remove('ov-title-collapsed');
        }
    }, []);

    useEffect(() => {
        const scrollEl = tableScrollRef.current;
        if (!scrollEl) return undefined;

        syncQuotesPageTitle();
        scrollEl.addEventListener('scroll', syncQuotesPageTitle, { passive: true });
        return () => {
            scrollEl.removeEventListener('scroll', syncQuotesPageTitle);
        };
    }, [syncQuotesPageTitle, filterTabOpen, filteredQuotes.length]);

    // Helper function to get one priority shipping date (earliest available)
    const getPriorityShippingDate = (quote) => {
        if (!quote?.lines?.length) return '-';
        const dates = quote.lines
            .map(line => line.promise_date)
            .filter(date => date)
            .map(date => {
                const parsedDate = new Date(date);
                return Number.isNaN(parsedDate.getTime()) ? null : { raw: date, timestamp: parsedDate.getTime() };
            })
            .filter(dateObj => dateObj !== null)
            .sort((a,b) => a.timestamp -b.timestamp);

        if (dates.length > 0) {
            return dates[0].raw;
        }

        const fallbackDate = quote.lines.find(line => line.promise_date)?.promise_date;
        return fallbackDate || '-';
    };

    // Helper function to get total quote value
    const getQuoteTotal = (quote) => {
        if (!quote?.lines?.length) return '-';
        const total = quote.lines.reduce((sum, line) => {
            return sum + (Number(line.request_resale) || 0);
        }, 0);
        return total > 0 ? formatCurrency(total) : '-';
    };

    const getQuoteColumnLine = useCallback((colId, quote) => {
        switch (colId) {
            case 'quote':
                return {
                    label: 'Quote #',
                    show: quote?.id != null,
                    content: (
                        <span className="d-inline-flex align-items-center gap-2">
                            <a
                                href={`https://gigaflightinc.cetecerp.com/quote/${quote.id}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                            >
                                {quote.id}
                            </a>
                            {renderRowActivity(quote.id)}
                            {quote.folder_url ? (
                                <a href={quote.folder_url} target="_blank" rel="noopener noreferrer" title="Open folder">
                                    <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: '#ff8c00' }}></i>
                                </a>
                            ) : (
                                <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: '#cccccc' }} title="No folder URL available"></i>
                            )}
                        </span>
                    ),
                };
            case 'owner': {
                const owner = getQuoteOwner(quote);
                return { label: 'Owner', content: owner, show: owner && owner !== '-' };
            }
            case 'customer_name':
                return {
                    label: 'Customer',
                    show: !!(quote.customer_name),
                    content: quote.customer_id ? (
                        <a
                            href={`https://gigaflightinc.cetecerp.com/customer/${quote.customer_id}/view`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            {quote.customer_name}
                        </a>
                    ) : (quote.customer_name || ''),
                };
            case 'created_on': {
                const created = getEarliestCreatedDate(quote);
                return { label: 'Created On', content: created, show: created && created !== '-' };
            }
            case 'time_in_stage': {
                const stage = formatTimeInStageLabel(getQuoteStageDateTime(quote));
                return { label: 'Time in Stage', content: stage, show: stage && stage !== '-' };
            }
            case 'lines':
                return { label: 'Lines #', content: quote?.lines?.length || 0, show: true };
            case 'shippingdate': {
                const shipDate = getPriorityShippingDate(quote);
                return { label: 'Shipping Date', content: shipDate, show: shipDate && shipDate !== '-' };
            }
            case 'quotetotal': {
                const total = getQuoteTotal(quote);
                return { label: 'Quote Total', content: total, show: total && total !== '-' };
            }
            default:
                return null;
        }
    }, [renderRowActivity]);

    const renderQuoteSetBodyCell = useCallback((quote) => (
        <td className="text-start orders-column-group-td quotes-col-quoteset">
            <CombinedColumnCell
                lines={['quote', 'owner', 'customer_name']
                    .map((colId) => getQuoteColumnLine(colId, quote))
                    .filter(Boolean)}
            />
        </td>
    ), [getQuoteColumnLine]);

    const renderCustomGroupBodyCell = useCallback((quote, mergedId) => {
        const groupId = mergedId.replace(/^group-/, '');
        const group = customGroups.find((g) => g.id === groupId);
        const lines = (group?.columnIds || [])
            .map((colId) => getQuoteColumnLine(colId, quote))
            .filter(Boolean);
        return (
            <td className="text-start orders-column-group-td quotes-col-merged">
                <CombinedColumnCell lines={lines} />
            </td>
        );
    }, [customGroups, getQuoteColumnLine]);

    const getMergedBodyCells = useCallback((quote) => {
        const cells = {};
        if (activeGroupId === QUOTE_SET_GROUP_ID) {
            cells[QUOTE_SET_MERGED_ID] = renderQuoteSetBodyCell(quote);
        }
        customGroups.forEach((group) => {
            if (activeGroupId === group.id) {
                const mergedId = getMergedIdForGroup(group.id);
                cells[mergedId] = renderCustomGroupBodyCell(quote, mergedId);
            }
        });
        return cells;
    }, [activeGroupId, customGroups, renderQuoteSetBodyCell, renderCustomGroupBodyCell]);

    const mergedHeaderCells = useMemo(() => {
        const cells = {};
        if (activeGroupId === QUOTE_SET_GROUP_ID) {
            cells[QUOTE_SET_MERGED_ID] = (
                <th
                    className="text-center quotes-col-quoteset"
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={() => handleColumnSort('quote')}
                >
                    QUOTE SET{getColumnSortIcon('quote')}
                </th>
            );
        }
        customGroups.forEach((group) => {
            const mergedId = getMergedIdForGroup(group.id);
            if (activeGroupId === group.id) {
                cells[mergedId] = (
                    <th className="text-center quotes-col-merged">
                        {group.name}
                    </th>
                );
            }
        });
        return cells;
    }, [activeGroupId, customGroups, columnSort]);

    const getQuoteComments = (quote) => {
        if (!quote) return [];
        const backendComments = quote.comment || [];
        const localComments = commentsMap[quote.id] || [];
        const mergedById = new Map();
        [...backendComments, ...localComments].forEach((comment) => {
            if (!comment || isQuoteLikeRecord(comment)) return;
            const key = getCommentId(comment) != null ? String(getCommentId(comment)) : `idx-${mergedById.size}`;
            if (String(key) === String(quote.id)) return;
            mergedById.set(key, comment);
        });
        return sortCommentsByDate(Array.from(mergedById.values()));
    };

    const isQuoteChatOpen = (quoteId) => {
        const key = getCommentThreadKey({ quoteId });
        return Boolean(commentDock?.threads?.some((thread) => thread.key === key && !thread.minimized));
    };

    const getQuoteStatusLabels = (quoteId) => {
        const labels = new Set();
        const add = (arr) => {
            if (!Array.isArray(arr)) return;
            arr.forEach((tag) => {
                const s = String(typeof tag === 'string' ? tag : tag?.label || '').toLowerCase().trim();
                if (s) labels.add(s);
            });
        };
        add(submittedQuoteStatuses[quoteId]);
        add(submittedQuoteStatuses[String(quoteId)]);
        add(quoteStatusSelections[quoteId]);
        add(quoteStatusSelections[String(quoteId)]);
        (quoteTagsToDeselect[quoteId] || quoteTagsToDeselect[String(quoteId)] || []).forEach((tag) => {
            const s = String(typeof tag === 'string' ? tag : tag?.label || '').toLowerCase().trim();
            if (s) labels.delete(s);
        });
        return labels;
    };

    // Whole quote row. Priority when more than one is selected:
    // 1 PO Review (charcoal), 2 Hot Order / AOG (orange),
    // 3 CC Hold or Wire Hold together with PPA (purple). Paid removes the purple highlight.
    const getQuoteRowHighlight = (quote) => {
        if (!quote?.id) return null;
        const labels = getQuoteStatusLabels(quote.id);
        if (labels.has('po review')) return 'po-review';
        if (labels.has('hot order') || labels.has('hot order/aog') || labels.has('aog')) return 'hot-order';
        const hasHold = labels.has('cc hold') || labels.has('wire hold');
        if (hasHold && labels.has('ppa') && !labels.has('paid')) return 'purple';
        return null;
    };

    const renderStickyNotes = (quote) => {
        const quoteNumber = quote?.id != null && quote.id !== '' ? String(quote.id) : '';
        return (
            <StickyNotesCell
                orderNumber={quoteNumber}
                customerName={quote?.customer_name}
                label={quoteNumber}
                notes={stickyNotes.notesByOrder[quoteNumber] || []}
                pinned={shippingInstructions.forQuote(quote)}
                shippingComments={(quote?.lines || []).map((line) => line?.order_comment2).filter((comment) => comment && String(comment).trim())}
                customerId={shippingInstructions.customerIdFor(quote)}
                onAddShipping={shippingInstructions.add}
                onUpdateShipping={shippingInstructions.update}
                onRemoveShipping={shippingInstructions.remove}
                onAdd={stickyNotes.add}
                onUpdate={stickyNotes.update}
                onRemove={stickyNotes.remove}
            />
        );
    };

    const renderQuoteComments = (quote) => {
        const comments = getQuoteComments(quote);
        const watchers = presenceUsersInCell(quoteNotesCell(quote.id));
        return (
            <HoverPO
                hideBadge
                comments={comments}
                quoteId={quote.id}
                orderNumber={quote.id}
                customerName={quote.customer_name}
                customerId={quote.customer_id}
                sourcePage="quotes"
                onVisibleChange={(isVisible) => handleNotesVisibility(quote.id, isVisible)}
                onDeleteComment={(commentId) => handleDeleteComment(quote.id, commentId)}
                onResolveThread={(commentId) => handleResolveThread(quote.id, commentId)}
                onAddComment={(newComment, status) => handleAddComment(quote.id, newComment, status)}
            >
                <OrderCommentsButton
                    total={comments.length}
                    unread={notificationsCtx ? notificationsCtx.unreadCountForQuote(quote.id) : 0}
                    presenceUsers={watchers}
                    active={isQuoteChatOpen(quote.id) || watchers.length > 0}
                />
            </HoverPO>
        );
    };

    return (
        <div className="order-validation-outer quotes-outer">
            <div ref={titleWrapRef} className="order-validation-page-title-wrap px-3 pt-3 pb-1">
                <h1 className="orders-dashboard-page-title table-section-text table-section-text-left mb-0">Quotes</h1>
            </div>
        <div className="box-body border b-white rounded-2 quotes-page-shell">
            <div className="quotes-sticky-stack">
            {/* Header section - Simplified without archive button */}
            <div className="p-2 m-0 border-bottom row align-items-center">
                <div className="col d-flex justify-content-start align-items-center gap-3">
                    <OrderRefresh
                        onRefresh={handleRefreshQuotes}
                        lastRefreshData={lastRefreshData}
                        isLoading={isRefreshing}
                        title="Quotes"
                    />
                    <PresenceBar
                        here={presence.here}
                        cells={presence.cells}
                        connected={presence.connected}
                        me={presence.me}
                    />
                </div>
                <div className="col-auto d-flex justify-content-end align-items-center gap-3 flex-wrap">
                    <div className="orders-dashboard-refresh-pill" role="status">
                        <span>
                            Auto-refresh in <strong>{timerFormatted}</strong>
                        </span>
                        <span className="orders-dashboard-refresh-meta">Next refresh in 10 minutes</span>
                    </div>
                    <button className="btn border-success d-flex align-items-center gap-2 px-3" onClick={() => { setFilterTab(!filterTabOpen) }} >
                        <img src={filterIcon} style={{ width: "16px", height: "16px" }} alt="filter" />
                        <span>Filter</span>
                    </button>
                    <button className="btn border-success d-flex align-items-center gap-2 px-3" onClick={() => { setExports(true); fetchQuotesData(currentPage - 1, itemsPerPage) }}>
                        <img src={excelIcon} style={{ width: "16px", height: "16px" }} alt="excel" />
                        <span>Export</span>
                    </button>
                    <button
                        type="button"
                        className="btn border-success d-flex align-items-center gap-2 px-3"
                        onClick={() => setShowColumnGroupsModal(true)}
                        title="Combine columns into groups"
                    >
                        <i className="fa fa-table-columns" aria-hidden="true"></i>
                        <span>Column Groups</span>
                    </button>
                    <ViewModeToggle
                        inlineDropdownMode={inlineDropdownMode}
                        onSelectView={handleSelectView}
                        defaultViewMode={defaultViewMode}
                        onSetDefault={handleSetViewModeDefault}
                    />
                </div>
            </div>

            {/* Filter section */}
            {filterTabOpen && (
                <div className="row p-2 m-0" style={{ background: '#d3d3d333' }}>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Quote ID</div>
                        <input 
                            type="text" 
                            className="form-control" 
                            style={{ width: '65%' }} 
                            placeholder="Enter Quote ID"
                            value={filterQuoteId}
                            onChange={(e) => setFilterQuoteId(e.target.value)}
                        />
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Customer</div>
                        <input 
                            type="text" 
                            className="form-control" 
                            style={{ width: '65%' }} 
                            placeholder="Enter Customer Name"
                            value={filterCustomer}
                            onChange={(e) => setFilterCustomer(e.target.value)}
                        />
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Status</div>
                        <select 
                            className="form-select" 
                            style={{ width: '65%' }}
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="Draft">Draft</option>
                            <option value="Open">Open</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Owner</div>
                        <input 
                            type="text" 
                            className="form-control" 
                            style={{ width: '65%' }} 
                            placeholder="Enter Owner"
                            value={filterOwner}
                            onChange={(e) => setFilterOwner(e.target.value)}
                        />
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Created On</div>
                        <DatePicker
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            style={{ width: '65%' }}
                            placeholderText='YYYY-MM-DD'
                            selected={createdOnDate ? new Date(createdOnDate) : null}
                            onChange={(date) => setCreatedOnDate(date ? date.toISOString().split('T')[0] : '')}
                            autoComplete='off'
                        />
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Shipping Date</div>
                        <DatePicker
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            style={{ width: '65%' }}
                            placeholderText='YYYY-MM-DD'
                            selected={shippingDate ? new Date(shippingDate) : null}
                            onChange={(date) => setShippingDate(date ? date.toISOString().split('T')[0] : '')}
                            autoComplete='off'
                        />
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <div className='px-2'>Quote Price</div>
                        <input 
                            type="text" 
                            className="form-control" 
                            style={{ width: '65%' }} 
                            placeholder="Enter Quote Price"
                            value={filterQuotePrice}
                            onChange={(e) => setFilterQuotePrice(e.target.value)}
                        />
                    </div>
                    <div className="col-lg-3 col-md-4 col-xs-6 pb-2 d-flex justify-content-end align-items-center">
                        <input type="button" className="btn btn-primary" value='Submit' onClick={applyFilters} />
                    </div>
                </div>
            )}

            {/* Controls section */}
            <div className='row p-2 align-items-center'>
                <div className="col-auto">
                    <select
                        className="form-select quotes-page-size"
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="0">All</option>
                    </select>
                </div>
                <div className="col-8 d-flex flex-wrap align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm reset-column-order-btn"
                        onClick={resetColumnOrder}
                        title="Reset column order to default"
                    >
                        <i className="fa fa-undo" style={{ marginRight: '4px' }}></i>
                        Reset Column Order
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm reset-column-order-btn"
                        onClick={() => setShowColumnVisibilityModal(true)}
                        title="Show or hide columns"
                    >
                        <i className="fa fa-columns" style={{ marginRight: '4px' }}></i>
                        Show/Hide Columns
                    </button>
                    {hiddenColumnCount > 0 && (
                        <button
                            type="button"
                            className="btn btn-sm reset-column-order-btn"
                            onClick={handleShowAllColumns}
                            title="Show all hidden columns"
                        >
                            <i className="fa fa-eye" style={{ marginRight: '4px' }}></i>
                            Show all columns ({hiddenColumnCount})
                        </button>
                    )}
                </div>
                <div className="col-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder={messages.search_label}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>
            </div>
            </div>

            {/* Table scroll region — thead sticks to top of this panel, not the viewport */}
            <div ref={tableScrollRef} className="quotes-table-scroll">
                    <table className="table gf-table table-bordered table-hover mb-0 quotes-table quotes-table-fit compact-data-table" style={{ '--quotes-table-width': `${quotesTableWidth}px` }}>
                        <thead className="sticky-top bg-light">
                            <tr>
                                <ReorderableCells
                                    order={effectiveColumnOrder}
                                    mergedCells={mergedHeaderCells}
                                    draggable
                                    dragHandlers={quotesColumnDragHandlers}
                                    draggedColumnId={draggedColumnId}
                                    columnVisibility={columnVisibility}
                                    onToggleColumn={toggleColumnVisibility}
                                    isHeader
                                >
                                    <th className="text-center quotes-col-info">INFO</th>
                                    <th className="text-center quotes-col-quote" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('quote')}>QUOTE{getColumnSortIcon('quote')}</th>
                                    <th className="text-center quotes-col-owner" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('owner')}>OWNER{getColumnSortIcon('owner')}</th>
                                    <th className="text-center quotes-col-customer_name" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('customer_name')}>CUSTOMER NAME{getColumnSortIcon('customer_name')}</th>
                                    <th className="text-center quotes-col-status" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('status')}>ORDER STATUS{getColumnSortIcon('status')}</th>
                                    <th className="text-center quotes-col-created_on" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('created_on')}>CREATED ON{getColumnSortIcon('created_on')}</th>
                                    <th className="text-center quotes-col-time_in_stage" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('time_in_stage')}>TIME IN STAGE{getColumnSortIcon('time_in_stage')}</th>
                                    <th className="text-center quotes-col-lines" style={{ cursor: 'pointer' }} onClick={() => handleColumnSort('lines')}>LINES #{getColumnSortIcon('lines')}</th>
                                    <th className="text-center quotes-col-comments">STICKY NOTES</th>
                                    <th className="text-center quotes-col-shippingdate">SHIPPING DATE</th>
                                    <th className="text-center quotes-col-quotetotal">QUOTE TOTAL</th>
                                    <th className="text-center quotes-col-quoteaccess">QUOTE ACCESS</th>
                                </ReorderableCells>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={effectiveColumnOrder.length} className="text-center">
                                        <div className="d-flex justify-content-center align-items-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <span className="ms-2">Loading quotes...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : currentPageData.length > 0 ? (
                                currentPageData.map((quote) => {
                                    const inlineExpanded = !!expandedInlineQuotes[quote.id];
                                    const rowHighlight = getQuoteRowHighlight(quote);
                                    const quoteRowClassName = rowHighlight === 'po-review'
                                        ? 'po-review-row'
                                        : rowHighlight === 'hot-order'
                                            ? 'hot-order-row'
                                            : rowHighlight === 'purple'
                                                ? 'cc-hold-ppa-row'
                                                : undefined;
                                    
                                    return (
                                        <React.Fragment key={quote.id}>
                                            <tr
                                                className={quoteRowClassName}
                                                style={
                                                    rowHighlight === 'po-review'
                                                        ? { backgroundColor: '#232323', color: '#ffffff' }
                                                        : rowHighlight === 'hot-order'
                                                            ? { backgroundColor: '#FF9D47' }
                                                            : rowHighlight === 'purple'
                                                                ? { backgroundColor: '#BEA9FF' }
                                                                : undefined
                                                }
                                            >
                                                <ReorderableCells
                                                    order={effectiveColumnOrder}
                                                    mergedCells={getMergedBodyCells(quote)}
                                                    columnVisibility={columnVisibility}
                                                >
                                                <td className="text-center quotes-col-info">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <button className="btn btn-link p-0"
                                                            title={inlineDropdownMode ? "Toggle inline quote info" : "View quote inventory info"}
                                                            onClick={() => handleEyeIconClick(quote)}>
                                                            <img src={viewIcon} alt="View" style={{ width: '20px' }} />
                                                        </button>
                                                        {renderQuoteComments(quote)}
                                                    </div>
                                                </td>
                                                <td className="text-center quotes-col-quote">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <a href={`https://gigaflightinc.cetecerp.com/quote/${quote.id}/view`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                            {quote.id}
                                                        </a>
                                                        {renderRowActivity(quote.id)}
                                                        {quote.folder_url ? (
                                                            <a href={quote.folder_url} target="_blank" rel="noopener noreferrer" title="Open folder">
                                                                <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: rowHighlight === 'hot-order' ? '#cc7a00' : '#ff8c00', cursor: 'pointer' }}></i>
                                                            </a>
                                                        ) : (
                                                            <i className="fa fa-folder" aria-hidden="true" style={{ fontSize: '16px', color: '#cccccc', cursor: 'not-allowed' }} title="No folder URL available"></i>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center quotes-col-owner">{getQuoteOwner(quote)}</td>
                                                <td className="quotes-col-customer_name">
                                                    {quote.customer_id ? (
                                                        <a
                                                            href={`https://gigaflightinc.cetecerp.com/customer/${quote.customer_id}/view`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ textDecoration: 'none' }}
                                                        >
                                                            {quote.customer_name || '-'}
                                                        </a>
                                                    ) : (
                                                        quote.customer_name || '-'
                                                    )}
                                                </td>
                                                <td className="text-center quotes-col-status">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {renderQuoteStatusPresence(quote.id)}
                                                        <div
                                                            className="form-control quote-status-trigger d-flex align-items-center justify-content-between flex-grow-1"
                                                            style={{
                                                                cursor: "pointer",
                                                                minHeight: "38px",
                                                                backgroundColor: "white",
                                                                border: "1px solid #ced4da",
                                                                minWidth: 0
                                                            }}
                                                            onClick={(e) => handleQuoteStatusTriggerClick(e, quote)}
                                                        >
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {(quoteStatusSelections[quote.id] || []).map((tag, index) => (
                                                                    <span
                                                                        key={`current-${index}`}
                                                                        className="badge"
                                                                        style={{
                                                                            backgroundColor: tag.color,
                                                                            color: "white",
                                                                            fontSize: "0.75rem",
                                                                            padding: "4px 8px",
                                                                            borderRadius: "12px",
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            gap: "4px"
                                                                        }}
                                                                    >
                                                                        {tag.icon && <i className={tag.icon} aria-hidden="true"></i>}
                                                                        {tag.label}
                                                                    </span>
                                                                ))}
                                                                {(submittedQuoteStatuses[quote.id] || []).map((tag, index) => {
                                                                    const isMarkedForDeselection = (quoteTagsToDeselect[quote.id] || []).some(t => t.label === tag.label);
                                                                    return (
                                                                        <span
                                                                            key={`submitted-${index}`}
                                                                            className="badge"
                                                                            style={{
                                                                                backgroundColor: isMarkedForDeselection ? "#6c757d" : tag.color,
                                                                                color: "white",
                                                                                fontSize: "0.75rem",
                                                                                padding: "4px 8px",
                                                                                borderRadius: "12px",
                                                                                cursor: "pointer",
                                                                                opacity: isMarkedForDeselection ? 0.6 : 1,
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: "4px"
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleQuoteTagDeselect(quote.id, tag);
                                                                            }}
                                                                            title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                                                                        >
                                                                            {tag.icon && <i className={tag.icon} aria-hidden="true"></i>}
                                                                            {tag.label}
                                                                        </span>
                                                                    );
                                                                })}
                                                                {(quoteStatusSelections[quote.id] || []).length === 0 && (submittedQuoteStatuses[quote.id] || []).length === 0 && (
                                                                    <span className="text-muted">Select tags...</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {hasQuoteStatusChangesToSubmit(quote.id) && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-success btn-sm"
                                                                style={{
                                                                    minWidth: "20px",
                                                                    height: "20px",
                                                                    padding: "0",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: "10px",
                                                                    borderRadius: "2px",
                                                                    flexShrink: 0
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleQuoteStatusSubmit(quote.id);
                                                                }}
                                                                title="Submit changes"
                                                            >
                                                                ✓
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center quotes-col-created_on">{getEarliestCreatedDate(quote)}</td>
                                                <td className="text-center quotes-col-time_in_stage">
                                                    {formatTimeInStageLabel(getQuoteStageDateTime(quote))}
                                                </td>
                                                <td className="text-center quotes-col-lines">{quote?.lines?.length || 0}</td>
                                                <td className="notes-column sticky-notes-column quotes-col-comments">
                                                    {renderStickyNotes(quote)}
                                                </td>
                                                <td className="text-center quotes-col-shippingdate">{getPriorityShippingDate(quote)}</td>
                                                <td className="text-center quotes-col-quotetotal">{getQuoteTotal(quote)}</td>
                                                <td className="quote-access-column quotes-col-quoteaccess">
                                                       <div className="d-flex flex-column align-items-center justify-content-center gap-2" style={{ width: '100%', maxWidth: '118px', margin: '0 auto' }}>
                                                        {(quoteAccessLinks[quote.id] || []).length > 0 && (
                                                            <div className="d-flex flex-wrap gap-1 justify-content-center" style={{ width: '100%' }}>
                                                                {quoteAccessLinks[quote.id].map((urlItem) => (
                                                                    <div
                                                                        key={urlItem.id}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            backgroundColor: '#f0f0f0',
                                                                            border: '1px solid #ddd',
                                                                            borderRadius: '12px',
                                                                            padding: '3px 6px',
                                                                            fontSize: '10px',
                                                                            maxWidth: '114px',
                                                                        }}
                                                                    >
                                                                        <a
                                                                            href={urlItem.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                textDecoration: 'none',
                                                                                color: '#007bff',
                                                                                fontWeight: '500',
                                                                                marginRight: '4px',
                                                                                flexGrow: 1,
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                            title={`${urlItem.urlName}: ${urlItem.url}`}
                                                                        >
                                                                            {urlItem.urlName}
                                                                        </a>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteQuoteAccessLink(quote.id, urlItem.id)}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: '#dc3545',
                                                                                cursor: 'pointer',
                                                                                padding: '0',
                                                                                marginLeft: '2px',
                                                                                fontSize: '12px',
                                                                                lineHeight: '1',
                                                                                width: '16px',
                                                                                height: '16px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center'
                                                                            }}
                                                                            title="Delete this URL"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            style={{ fontSize: '0.7rem', padding: '3px 8px', height: '28px' }}
                                                            onClick={() => handleQuoteAccessClick(quote)}
                                                            title="Add quote access link"
                                                        >
                                                            <i className="fa fa-plus" style={{ marginRight: '3px' }}></i>
                                                            Add
                                                        </button>
                                                    </div>
                                                </td>
                                                </ReorderableCells>
                                            </tr>
                                            {inlineDropdownMode && inlineExpanded && (
                                                <tr>
                                                    <td colSpan={effectiveColumnOrder.length} className="quotes-inline-panel" style={{ padding: '6px 8px', backgroundColor: 'var(--bridge-surface, #ffffff)', color: 'var(--bridge-text, #212529)' }}>
                                                        <div style={{ padding: '4px 0' }}>
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <h6 className="mb-0">
                                                                    Quote #
                                                                    <a
                                                                        href={`https://gigaflightinc.cetecerp.com/quote/${quote.id}/view`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ textDecoration: 'none' }}
                                                                    >
                                                                        {quote.id}
                                                                    </a>
                                                                    {' '} - Inventory Information
                                                                </h6>
                                                                <span className="badge bg-info">{quote.status || '-'}</span>
                                                            </div>
                                                            {quote.lines?.length > 0 ? (
                                                                <div style={{ overflowX: 'auto', overflowY: 'visible', textAlign: 'left' }}>
                                                                    <table
                                                                        className="table table-sm gf-table table-bordered compact-data-table quotes-inventory-table mb-0"
                                                                        style={{ width: 'fit-content', minWidth: '1650px', backgroundColor: '#ffffff', tableLayout: 'fixed', display: 'inline-table', marginLeft: 0, marginRight: 'auto' }}
                                                                    >
                                                                        <colgroup>
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col className="quotes-inventory-part" />
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col className="quotes-inventory-qty" />
                                                                            <col style={{ width: '220px' }} />
                                                                        </colgroup>
                                                                        <thead className="table-dark">
                                                                            <tr>
                                                                                <th className="quotes-inventory-qty">Line #</th>
                                                                                <th className="quotes-inventory-part">Part No</th>
                                                                                <th className="quotes-inventory-qty">QOO</th>
                                                                                <th className="quotes-inventory-qty">QR</th>
                                                                                <th className="quotes-inventory-qty">QOH</th>
                                                                                <th className="quotes-inventory-qty">Available</th>
                                                                                <th className="quotes-inventory-qty">Quantity</th>
                                                                                <th className="quotes-inventory-qty">Request Resale</th>
                                                                                <th style={{ whiteSpace: 'nowrap' }}>Line Status</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {quote.lines.map((line, idx) => {
                                                                                const qoh = Number(line.part?.qoh_cache) || 0;
                                                                                const qr = Number(line.part?.qr_cache) || 0;
                                                                                const qoo = Number(line.part?.qoo_cache) || 0;
                                                                                const qtyInHand = qoh - qr;
                                                                                const hasPartInfo = line.part && (line.part.qoh_cache !== undefined || line.part.qoo_cache !== undefined || line.part.qr_cache !== undefined);

                                                                                return (
                                                                                    <tr key={idx} style={{ backgroundColor: '#ffffff' }}>
                                                                                        <td className="quotes-inventory-qty">{idx + 1}</td>
                                                                                        <td className="quotes-inventory-part">
                                                                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                                                                <span>{formatPartNumber(line.prcpart || line.part?.part_number)}</span>
                                                                                                {line.part?.id && (
                                                                                                    <div className="position-relative">
                                                                                                        <button
                                                                                                            className="btn btn-sm btn-outline-secondary py-0 px-1 inline-part-link-trigger"
                                                                                                            type="button"
                                                                                                            aria-expanded={inlinePartDropdown.open && inlinePartDropdown.partId === line.part.id}
                                                                                                            title="View Part Links"
                                                                                                            onClick={(event) => handleInlinePartDropdownToggle(line, event)}
                                                                                                        >
                                                                                                            <i className="fa fa-link" aria-hidden="true"></i>
                                                                                                        </button>
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="text-center quotes-inventory-qty">
                                                                                            {hasPartInfo ? (
                                                                                                <>
                                                                                                    {qoo}
                                                                                                    {qoo !== 0 && line.part?.id && (
                                                                                                        <a href={`https://gigaflightinc.cetecerp.com/part/${line.part.id}/pos`} target="_blank" rel="noreferrer" title="View Purchase Orders" className="ms-2">
                                                                                                            <i className="fa fa-external-link" aria-hidden="true"></i>
                                                                                                        </a>
                                                                                                    )}
                                                                                                </>
                                                                                            ) : (
                                                                                                <span className="text-muted">N/A</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="text-center quotes-inventory-qty">{hasPartInfo ? qr : <span className="text-muted">N/A</span>}</td>
                                                                                        <td className="text-center quotes-inventory-qty">{hasPartInfo ? qoh : <span className="text-muted">N/A</span>}</td>
                                                                                        <td className="text-center quotes-inventory-qty">
                                                                                            {hasPartInfo ? (
                                                                                                <span className={qtyInHand < 0 ? 'text-danger fw-bold' : ''}>
                                                                                                    {qtyInHand}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-muted">N/A</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td className="text-center quotes-inventory-qty">{line.orig_qty != null ? Number(line.orig_qty).toFixed(3) : '-'}</td>
                                                                                        <td className="text-center quotes-inventory-qty">{line.request_resale != null ? Number(line.request_resale).toFixed(2) : '-'}</td>
                                                                                        <td>
                                                                                            <div className="d-flex align-items-center gap-2">
                                                                                                <div
                                                                                                    className="form-control quote-line-status-trigger d-flex align-items-center"
                                                                                                    style={{
                                                                                                        cursor: "pointer",
                                                                                                        minHeight: "30px",
                                                                                                        backgroundColor: "white",
                                                                                                        border: "1px solid #ced4da",
                                                                                                        padding: "2px 6px"
                                                                                                    }}
                                                                                                    onClick={(e) => toggleQuoteLineStatusDropdown(e, line.id)}
                                                                                                >
                                                                                                    <div className="d-flex flex-wrap gap-1">
                                                                                                        {(quoteLineStatusSelections[line.id] || []).map((tag, tagIndex) => (
                                                                                                            <span
                                                                                                                key={`current-${tagIndex}`}
                                                                                                                className="badge"
                                                                                                                style={{ backgroundColor: tag.color, color: 'white', fontSize: '0.7rem', padding: '3px 6px', borderRadius: '10px' }}
                                                                                                            >
                                                                                                                {tag.label}
                                                                                                            </span>
                                                                                                        ))}
                                                                                                        {(submittedQuoteLineStatuses[line.id] || []).map((tag, tagIndex) => {
                                                                                                            const isMarkedForDeselection = (quoteLineTagsToDeselect[line.id] || []).some(t => t.label === tag.label);
                                                                                                            return (
                                                                                                                <span
                                                                                                                    key={`submitted-${tagIndex}`}
                                                                                                                    className="badge"
                                                                                                                    style={{
                                                                                                                        backgroundColor: isMarkedForDeselection ? '#6c757d' : tag.color,
                                                                                                                        color: 'white',
                                                                                                                        fontSize: '0.7rem',
                                                                                                                        padding: '3px 6px',
                                                                                                                        borderRadius: '10px',
                                                                                                                        cursor: 'pointer',
                                                                                                                        opacity: isMarkedForDeselection ? 0.6 : 1,
                                                                                                                    }}
                                                                                                                    onClick={(e) => {
                                                                                                                        e.stopPropagation();
                                                                                                                        handleQuoteLineTagDeselect(line.id, tag);
                                                                                                                    }}
                                                                                                                    title={isMarkedForDeselection ? "Click to keep this tag" : "Click to deselect this tag"}
                                                                                                                >
                                                                                                                    {tag.label}
                                                                                                                </span>
                                                                                                            );
                                                                                                        })}
                                                                                                        {(quoteLineStatusSelections[line.id] || []).length === 0 && (submittedQuoteLineStatuses[line.id] || []).length === 0 && (
                                                                                                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Select tags...</span>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                                {renderQuoteLinePresence(line.id)}
                                                                                                {hasQuoteLineChangesToSubmit(line.id) && (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        className="btn btn-success btn-sm"
                                                                                                        style={{ minWidth: "20px", height: "20px", padding: "0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", borderRadius: "2px" }}
                                                                                                        disabled={Boolean(quoteLineLockOwner(line.id))}
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            handleQuoteLineStatusSubmit(line.id);
                                                                                                        }}
                                                                                                        title={
                                                                                                            quoteLineLockOwner(line.id)
                                                                                                                ? `${quoteLineLockOwner(line.id).name} is editing this line status`
                                                                                                                : "Submit changes"
                                                                                                        }
                                                                                                    >
                                                                                                        ✓
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ) : (
                                                                <p className="text-center text-muted py-2 mb-0">No line items available.</p>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={effectiveColumnOrder.length} className="text-center text-muted">
                                        No quotes found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
            </div>

            {quoteStatusDropdownOpenId && (
                <OrderStatusDropdown
                    visible={true}
                    position={quoteStatusDropdownPosition}
                    orderStatuses={QUOTE_STATUS_TAGS}
                    selectedTags={quoteStatusSelections[quoteStatusDropdownOpenId] || []}
                    onToggleTag={(tag) => handleQuoteStatusToggle(quoteStatusDropdownOpenId, tag)}
                    onClose={() => setQuoteStatusDropdownOpenId(null)}
                    menuClassName="quote-order-status-menu"
                />
            )}
            {quoteLineStatusDropdownOpenId && (
                <OrderStatusDropdown
                    visible={true}
                    position={quoteLineStatusDropdownPosition}
                    orderStatuses={QUOTE_LINE_STATUS_TAGS}
                    selectedTags={quoteLineStatusSelections[quoteLineStatusDropdownOpenId] || []}
                    onToggleTag={(tag) => handleQuoteLineStatusToggle(quoteLineStatusDropdownOpenId, tag)}
                    onClose={() => setQuoteLineStatusDropdownOpenId(null)}
                    menuClassName="quote-line-status-menu"
                />
            )}

            {/* Pagination */}
            <div className="quotes-pagination row m-0">
                <div className="col-12 py-2">
                    <nav aria-label="Page navigation" className="d-flex justify-content-between align-items-center">
                        <span>
                            {itemsPerPage === 0 ? (
                                `Showing all ${filteredQuotes.length} records`
                            ) : (
                                <>
                                    Showing {paginationStart} to {paginationEnd} of {paginationOf}
                                </>
                            )}
                        </span>

                        {itemsPerPage > 0 && filteredQuotes.length > 0 && (
                            <ul className="pagination mb-0 align-items-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                                        Previous
                                    </button>
                                </li>

                                <li className="page-item">
                                    <select
                                        className="form-select mx-2"
                                        style={{ width: 'auto' }}
                                        value={currentPage}
                                        onChange={(e) => handlePageChange(Number(e.target.value))}
                                    >
                                        {[...Array(totalPages)].map((_, index) => (
                                            <option key={index} value={index + 1}>
                                                Page {index + 1}
                                            </option>
                                        ))}
                                    </select>
                                </li>

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                                        Next
                                    </button>
                                </li>
                            </ul>
                        )}
                    </nav>
                </div>
            </div>

            {/* First Modal - Basic Quote Details */}
            {selectedQuote && (
                <Modal className="quotes-inventory-modal" show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Quote #
                            <a
                                href={`https://gigaflightinc.cetecerp.com/quote/${selectedQuote.id}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="quotes-quote-number-link"
                            >
                                {selectedQuote.id}
                            </a>
                            {' '} Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5>
                            Customer:{' '}
                            {selectedQuote.customer_id ? (
                                <a
                                    href={`https://gigaflightinc.cetecerp.com/customer/${selectedQuote.customer_id}/view`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none' }}
                                >
                                    {selectedQuote.customer_name}
                                </a>
                            ) : (
                                selectedQuote.customer_name
                            )}{' '}
                            (ID: {selectedQuote.customer_id})
                        </h5>
                        <p>Status: {selectedQuote.status}</p>

                        <hr />
                        <h6>Lines:</h6>
                        {selectedQuote.lines?.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table gf-table table-bordered table-striped compact-data-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Line</th>
                                            <th>Part No</th>
                                            <th>Ship Date</th>
                                            <th>Ship Qty</th>
                                            <th>Request Resale</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedQuote.lines.map((line, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td>{formatPartNumber(line.prcpart || line.part?.part_number)}</td>
                                                <td>{line.promise_date || '-'}</td>
                                                <td>{line.orig_qty != null ? Number(line.orig_qty).toFixed(3) : '-'}</td>
                                                <td>{line.request_resale != null ? Number(line.request_resale).toFixed(2) : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p>No line items available.</p>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Close
                        </Button>
                        <Button variant="info" onClick={() => {
                            setShowModal(false);
                            handleViewQuoteInfo(selectedQuote);
                        }}>
                            View Inventory Info
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Second Modal - Quote Inventory Info */}
            {selectedQuoteDetails && (
                <Modal className="quotes-inventory-modal" show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="xl" enforceFocus={false}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Quote #
                            <a
                                href={`https://gigaflightinc.cetecerp.com/quote/${selectedQuoteDetails.id}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="quotes-quote-number-link"
                            >
                                {selectedQuoteDetails.id}
                            </a>
                            {' '} - Inventory Information
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {modalLoading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading inventory details...</p>
                            </div>
                        ) : (
                            <>
                                <div className="row mb-3 align-items-start">
                                    <div className="col-md-6">
                                        <h5>
                                            Customer:{' '}
                                            {selectedQuoteDetails.customer_id ? (
                                                <a
                                                    href={`https://gigaflightinc.cetecerp.com/customer/${selectedQuoteDetails.customer_id}/view`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ textDecoration: 'none' }}
                                                >
                                                    {selectedQuoteDetails.customer_name}
                                                </a>
                                            ) : (
                                                selectedQuoteDetails.customer_name
                                            )}
                                        </h5>
                                    </div>
                                    <div className="col-md-6 d-flex flex-column align-items-end">
                                        <p className="text-end mb-1">Status: <span className="badge bg-info">{selectedQuoteDetails.status}</span></p>
                                        {renderQuoteComments(selectedQuoteDetails)}
                                    </div>
                                </div>

                                <hr />
                                <h6>Quote Lines with Inventory Details:</h6>
                                {selectedQuoteDetails.lines?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table gf-table table-bordered table-striped compact-data-table quotes-inventory-table">
                                            <colgroup>
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                                <col className="quotes-inventory-qty" />
                                            </colgroup>
                                            <thead className="table-dark">
                                                <tr>
                                                    <th className="quotes-inventory-qty">Line #</th>
                                                    <th className="quotes-inventory-qty">Part No</th>
                                                    <th className="quotes-inventory-qty">QOO</th>
                                                    <th className="quotes-inventory-qty">QR</th>
                                                    <th className="quotes-inventory-qty">QOH</th>
                                                    <th className="quotes-inventory-qty">Available</th>
                                                    <th className="quotes-inventory-qty">Quantity</th>
                                                    <th className="quotes-inventory-qty">Request Resale</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedQuoteDetails.lines.map((line, idx) => {
                                                    // Calculate inventory values
                                                    const qoh = Number(line.part?.qoh_cache) || 0;
                                                    const qr = Number(line.part?.qr_cache) || 0;
                                                    const qoo = Number(line.part?.qoo_cache) || 0;
                                                    const qtyInHand = qoh - qr;

                                                    // Check if we have part information
                                                    const hasPartInfo = line.part && (line.part.qoh_cache !== undefined || line.part.qoo_cache !== undefined || line.part.qr_cache !== undefined);

                                                    return (
                                                        <tr key={idx}>
                                                            <td className="quotes-inventory-qty">{idx + 1}</td>
                                                            <td className="quotes-inventory-qty">
                                                                <div className="d-flex align-items-center">
                                                                    <span>{formatPartNumber(line.prcpart || line.part?.part_number)}</span>
                                                                    {line.part?.id && (
                                                                        <div className="position-relative d-inline-block ms-2">
                                                                            <button
                                                                                className="btn btn-sm btn-outline-secondary py-0 px-1 inline-part-link-trigger"
                                                                                type="button"
                                                                                aria-expanded={inlinePartDropdown.open && inlinePartDropdown.partId === line.part.id}
                                                                                title="View Part Links"
                                                                                onClick={(event) => handleInlinePartDropdownToggle(line, event)}
                                                                            >
                                                                                <i className="fa fa-link" aria-hidden="true"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center quotes-inventory-qty">
                                                                {hasPartInfo ? (
                                                                    <>
                                                                        {qoo}
                                                                        {qoo !== 0 && line.part?.id && (
                                                                            <a href={`https://gigaflightinc.cetecerp.com/part/${line.part.id}/pos`} target="_blank" rel="noreferrer" title="View Purchase Orders" className="ms-2">
                                                                                <i className="fa fa-external-link" aria-hidden="true"></i>
                                                                            </a>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>
                                                            <td className="text-center quotes-inventory-qty">{hasPartInfo ? qr : <span className="text-muted">N/A</span>}</td>
                                                            <td className="text-center quotes-inventory-qty">{hasPartInfo ? qoh : <span className="text-muted">N/A</span>}</td>
                                                            <td className="text-center quotes-inventory-qty">
                                                                {hasPartInfo ? (
                                                                    <span className={qtyInHand < 0 ? 'text-danger fw-bold' : ''}>
                                                                        {qtyInHand}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>
                                                            <td className="text-center quotes-inventory-qty">{line.orig_qty != null ? Number(line.orig_qty).toFixed(3) : '-'}</td>
                                                            <td className="text-center quotes-inventory-qty">{line.request_resale != null ? Number(line.request_resale).toFixed(2) : '-'}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-center text-muted py-3">No line items available.</p>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Third Modal - Manage Quote Access Links */}
            {selectedQuoteForAccess && (
                <Modal show={showQuoteAccessModal} onHide={handleCloseQuoteAccessModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Manage Quote #{selectedQuoteForAccess.id} Access Links</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {(quoteAccessLinks[selectedQuoteForAccess.id] || []).length > 0 ? (
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {(quoteAccessLinks[selectedQuoteForAccess.id] || []).map((link) => (
                                    <span
                                        key={link.id}
                                        className="badge bg-light text-dark border d-flex align-items-center gap-2"
                                        style={{ fontSize: '0.85rem', padding: '6px 10px' }}
                                    >
                                        <a href={link.url} target="_blank" rel="noopener noreferrer">{link.urlName || link.url}</a>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            style={{ fontSize: '0.6rem' }}
                                            title="Delete link"
                                            onClick={() => handleDeleteQuoteAccessLink(selectedQuoteForAccess.id, link.id)}
                                        ></button>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted mb-3">No access links added yet.</p>
                        )}
                        <div className="mb-2">
                            <label className="form-label">URL Name</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Customer Portal"
                                value={newQuoteLinkUrlName}
                                onChange={(e) => setNewQuoteLinkUrlName(e.target.value)}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="form-label">URL</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="https://..."
                                value={newQuoteLinkUrl}
                                onChange={(e) => setNewQuoteLinkUrl(e.target.value)}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseQuoteAccessModal}>
                            Close
                        </Button>
                        <Button variant="primary" onClick={handleAddQuoteAccessLink}>
                            Add URL
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
            {inlinePartDropdown.open && inlinePartDropdown.partId && createPortal(
                <div
                    className="part-link-dropdown-menu"
                    style={{
                        position: 'fixed',
                        top: `${inlinePartDropdown.top}px`,
                        left: `${inlinePartDropdown.left}px`,
                        minWidth: '180px',
                        zIndex: 1000005,
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '4px 0'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <a
                        className="dropdown-item d-flex align-items-center"
                        href={`https://gigaflightinc.cetecerp.com/part/${inlinePartDropdown.partId}/inventory`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '8px 14px', textDecoration: 'none', color: '#212529' }}
                    >
                        <i className="fa fa-boxes me-2" aria-hidden="true"></i>Inventory
                    </a>
                    <a
                        className="dropdown-item d-flex align-items-center"
                        href={`https://gigaflightinc.cetecerp.com/part/${inlinePartDropdown.partId}/waterfall`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '8px 14px', textDecoration: 'none', color: '#212529' }}
                    >
                        <i className="fa fa-chart-line me-2" aria-hidden="true"></i>Waterfall
                    </a>
                    <a
                        className="dropdown-item d-flex align-items-center"
                        href={`https://gigaflightinc.cetecerp.com/part/${inlinePartDropdown.partId}/pos`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '8px 14px', textDecoration: 'none', color: '#212529' }}
                    >
                        <i className="fa fa-shopping-cart me-2" aria-hidden="true"></i>Purchase Orders
                    </a>
                </div>,
                document.body
            )}
            <ColumnGroupsModal
                show={showColumnGroupsModal}
                onHide={() => setShowColumnGroupsModal(false)}
                activeGroupId={activeGroupId}
                defaultGroupId={defaultGroupId}
                customGroups={customGroups}
                onApply={handleColumnGroupsApply}
                onSaveCustomGroup={handleSaveCustomGroup}
                onDeleteCustomGroup={handleDeleteCustomGroup}
                groupableColumnIds={GROUPABLE_COLUMN_IDS}
                columnLabels={COLUMN_LABELS}
                builtinGroups={[{ id: QUOTE_SET_GROUP_ID, name: 'Quote Set' }]}
            />

            <ColumnVisibilityModal
                show={showColumnVisibilityModal}
                onHide={() => setShowColumnVisibilityModal(false)}
                columns={columnVisibilityList}
                visibleColumns={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                pageName="Quotes"
            />

            <FloatingRefreshButton
                onRefresh={handleManualPageRefresh}
                ariaLabel="Refresh Quotes"
                hoverLabel="Refresh"
            />
        </div>
        </div>
    );
};

export default Quotes;